// messages.ts

// ============================================================================
// GRN (Goods Received Note) Messages
// ============================================================================

export const MSG_GRN_SUCCESS: string = "GRN Created Successfully!";
export const MSG_GRN_FAILED: string = "Failed to create GRN. Please try again.";

export const MSG_GRN_SAVE: string = "GRN Saved Successfully!";
export const MSG_GRN_SAVE_FAILED: string = "Failed to save GRN. Please try again.";

export const MSG_GRN_HOLD: string = "GRN Hold Successfully!";
export const MSG_GRN_HOLD_FAILED: string = "Failed to hold GRN. Please try again.";

export const MSG_GRN_UPDATE: string = "GRN Updated Successfully!";
export const MSG_GRN_UPDATE_FAILED: string = "Failed to update GRN. Please try again.";

export const MSG_GRN_DELETE: string = "Item Deleted Successfully!";
export const MSG_GRN_DELETE_FAILED: string = "Failed to Delete Item. Please try again.";

// ============================================================================
// Authentication Messages
// ============================================================================

export const LOGIN_SUCCESS: string = "Hi, Welcome Back!";
export const LOGIN_FAILED: string = "Login failed. Please check your credentials and try again.!";

// ============================================================================
// Import Data Messages
// ============================================================================

export const MSG_INVALID_FILE_TYPE: string = "Please upload a valid Excel file (.xls or .xlsx)";
export const MSG_TEMPLATE_DOWNLOADED: string = "Template downloaded successfully!";
export const MSG_SELLING_INVENTORY_MUST_BE_YES_NO =
  "Must be Yes or No only.";

export const MSG_AT_LEAST_ONE_ITEM_TYPE_YES =
  "At least one of Selling Item or Inventory Item must be Yes.";
/**
 * Returns a message indicating file size exceeded limit
 * @param maxSizeMB - Maximum allowed file size in MB
 * @returns Formatted error message
 */
export const getMsgFileSizeExceeded = (maxSizeMB: number): string => 
  `File size exceeds ${maxSizeMB}MB limit. Please upload a smaller file.`;

/**
 * Returns a success message for imported items
 * @param count - Number of items successfully imported
 * @returns Formatted success message
 */
export const getSuccessItemsImported = (count: number): string => 
  `${count} item(s) imported successfully`;

// ============================================================================
// Validation Messages
// ============================================================================

export const VALIDATION_MANDATORY_MAPPING: string = "Mandatory fields are required.";
export const VALIDATION_SKU_DUPLICATE: string = "Duplicate SKUs found in import batch";
export const VALIDATION_TEMPLATE_MISMATCH: string = "Template validation failed. Headers do not match expected template structure.";

export const MSG_IMPORT_COMPLETE: string = "Import Complete";
export const MSG_ITEM_RECORD_EXISTS: string = "Items record already exists.";
export const MSG_ALREADY_EXISTS_IN_SYSTEM: string = "Already exists in the system";
export const MSG_DUPLICATE_IN_IMPORT_FILE: string = "Duplicate row in import file.";
export const MSG_UNIT_PER_PACKAGE_MUST_BE_NUMBER: string = "Unit Per Package must be a number.";
export const MSG_MANDATORY_FIELDS_MISSING: string = "Mandatory field(s) missing.";
export const MSG_CONFIRM_CONTINUE_IMPORT: string = "Only valid data records will be processed. Invalid records will be ignored, entirely empty rows will be removed, and duplicate records will be excluded.";
export const MSG_EXCLUDE_CONFIRM: string = "You have selected records to exclude from this import. These records will not be imported and can be uploaded later. Do you want to continue?";
export const MSG_NO_HEADER_WARNING: string = "You are mapping data fields without field names. Empty or blank cells in the 1st row may result in incorrect column data capturing during bulk upload. Any incorrect data field mapping cannot be reversed later.";
export const MSG_NO_ERROR_RECORDS: string = "No error records available for download.";
export const MSG_INVALID_FILE_FORMAT_XLS: string = "Invalid file format. Please upload an XLS, XLSX or CSV file.";
export const MSG_NO_DATA_IN_FILE: string = "The uploaded file contains no data.";
export const MSG_UNABLE_SAVE_CHANGES: string = "Unable to save changes. Please try again.";
export const MSG_SUBMISSION_FAILED: string = "Submission failed due to a system error. Please try again later.";
export const MSG_UNABLE_DELETE_RECORD: string = "Unable to delete the record. Please try again.";
export const MSG_RECORD_REMOVED_SUCCESS: string = "Record(s) removed successfully.";
export const MSG_FIELD_ALREADY_MAPPED: string = "This field has mapped with another one.";
export const MSG_ERROR_REPORT_FAILED: string = "Error report generation failed. Please try again.";

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_PARSING_EXCEL: string = "Error parsing Excel file";
export const ERROR_MERGED_CELLS: string = "Merged cells detected. Please unmerge cells before importing";
export const ERROR_INVALID_START_POINT: string = "Invalid starting point. Please check row and column values";
export const ERROR_NO_DATA: string = "No data found in Excel file";
export const ERROR_FAILED_TO_FETCH_DATA: string = "Failed to Fetch Data";

// ============================================================================
// Item Management Messages
// ============================================================================

export const MSG_ITEMS_LOAD_FAILED: string = "Failed to load items. Please try again.";

/**
 * Returns a success message for item deletion
 * @param itemName - Name of the deleted item
 * @returns Formatted success message
 */
export const MSG_ITEM_DELETE_SUCCESS = (itemName: string): string => 
  `Item "${itemName}" deleted successfully`;

export const MSG_ITEM_DELETE_FAILED: string = "Failed to delete item. ";
export const MSG_ITEM_DELETE_OPERATION_FAILED: string = "Delete operation failed";
export const MSG_ITEM_DELETE_NO_RESPONSE: string = "No response received from server.";
export const MSG_ITEM_DELETE_UNKNOWN_ERROR: string = "Unknown error occurred.";

// ============================================================================
// Export Messages
// ============================================================================

export const MSG_EXPORT_NO_DATA: string = "No data to export";
export const MSG_EXPORT_SUCCESS: string = "Export completed successfully";
export const MSG_EXPORT_FAILED: string = "Failed to export data";
export const MSG_EXPORT_SELECT_FIELD: string = "Please select at least one field to export";

// ============================================================================
// Item Create/Edit Messages
// ============================================================================

export const MSG_ITEM_VALIDATION_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_ITEM_NAVIGATION_ERROR: string = "An error occurred while navigating to image upload page.";

// ============================================================================
// Brand Management Messages
// ============================================================================

export const MSG_BRAND_CREATE_SUCCESS: string = "Brand Created Successfully!";
export const MSG_BRAND_UPDATE_SUCCESS: string = "Brand Updated Successfully!";
export const MSG_BRAND_DELETE_SUCCESS: string = "Brand deleted successfully!";
export const MSG_BRAND_DELETE_FAILED: string = "Failed to delete brand. Please try again.";
export const MSG_BRAND_DELETE_IN_USE: string = "Brand cannot be deleted. It's already in use";
export const MSG_BRAND_ALREADY_EXISTS: string = "Brand Already Exists!";
export const MSG_BRAND_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_BRAND_NAME_REQUIRED: string = "Brand Name is required";
export const MSG_BRAND_UNEXPECTED_ERROR: string = "An unexpected error occurred.";
export const MSG_BRAND_FETCH_FAILED: string = "Failed to fetch brands. Please try again.";
export const MSG_BRAND_SEARCH_PLACEHOLDER: string = "Search by...";
export const MSG_BRAND_INLINE_EDIT_NOT_SUPPORTED: string = "Inline editing is not supported for brands";
export const MSG_BRAND_CONFIRM_DELETE: string = "Are you sure you want to delete this brand?";
export const MSG_BRAND_CONFIRM_DELETE_NAMED: string = 'Are you sure you want to delete the brand "{brandName}"?';

// ============================================================================
// Main Category Management Messages - COMPLETE with all UI elements
// ============================================================================

// Page Titles
export const UI_MAINCAT_VIEW_TITLE: string = "Item Main Category";
export const UI_MAINCAT_BREADCRUMB_VIEW: string = "Main Category";
export const UI_MAINCAT_TABLE_TITLE: string = "Main Category";

// Success Messages
export const MSG_MAINCAT_CREATE_SUCCESS: string = "Main Category Created Successfully!";
export const MSG_MAINCAT_UPDATE_SUCCESS: string = "Main Category Updated Successfully!";
export const MSG_MAINCAT_DELETE_SUCCESS: string = 'Main Category "{0}" deleted successfully';

// Warning Messages
export const MSG_MAINCAT_ALREADY_EXISTS: string = "Main Category Already Exists!";
export const MSG_MAINCAT_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_MAINCAT_NAME_REQUIRED: string = "Main Category Name is required";
export const MSG_MAINCAT_NO_CATEGORY_SELECTED: string = "No category selected";

// Error Messages
export const MSG_MAINCAT_FETCH_FAILED: string = "Failed to fetch main categories.";
export const MSG_MAINCAT_DELETE_FAILED: string = "Failed to delete main category.";
export const MSG_MAINCAT_UNEXPECTED_ERROR: string = "An unexpected error occurred.";
export const MSG_MAINCAT_COMPCODE_MISSING: string = "Company code is missing";
export const MSG_MAINCAT_LOCCODE_MISSING: string = "Location code is missing";
export const MSG_MAINCAT_USERNAME_MISSING: string = "Username is missing";

// UI Elements
export const UI_MAINCAT_SEARCH_PLACEHOLDER: string = "Search by...";
export const UI_MAINCAT_DELETE_TITLE: string = "Confirm Delete";
export const UI_MAINCAT_DELETE_CONFIRMATION: string = "Are you sure you want to delete this main category?";
export const UI_MAINCAT_DELETE_CONFIRMATION_NAMED: string = 'Are you sure you want to delete the main category "{0}"?';
export const UI_MAINCAT_DELETING: string = "Deleting...";
export const UI_MAINCAT_ADD_BUTTON: string = "Add";
export const UI_MAINCAT_EDIT_ACTION: string = "Edit";
export const UI_MAINCAT_DELETE_ACTION: string = "Delete";

// Column Headers
export const UI_MAINCAT_COL_CODE: string = "Code";
export const UI_MAINCAT_COL_NAME: string = "Name";
export const UI_MAINCAT_COL_DESCRIPTION: string = "Description";

// ============================================================================
// Category Management Messages - COMPLETE with all UI elements
// ============================================================================

// Page Titles
export const UI_CATEGORY_VIEW_TITLE: string = "Category";
export const UI_CATEGORY_BREADCRUMB_VIEW: string = "Category";
export const UI_CATEGORY_TABLE_TITLE: string = "Category";

// Success Messages
export const MSG_CATEGORY_CREATE_SUCCESS: string = "Category Created Successfully!";
export const MSG_CATEGORY_UPDATE_SUCCESS: string = "Category Updated Successfully!";
export const MSG_CATEGORY_DELETE_SUCCESS: string = 'Category "{0}" deleted successfully';

// Warning Messages
export const MSG_CATEGORY_ALREADY_EXISTS: string = "Category already exists under this Main Category!";
export const MSG_CATEGORY_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_CATEGORY_NAME_REQUIRED: string = "Category Name is required";
export const MSG_CATEGORY_MAIN_CATEGORY_REQUIRED: string = "Main Category is required";
export const MSG_CATEGORY_NO_CATEGORY_SELECTED: string = "No category selected";

// Error Messages
export const MSG_CATEGORY_FETCH_FAILED: string = "Failed to fetch main categories.";
export const MSG_CATEGORY_FETCH_CATEGORIES_FAILED: string = "Failed to fetch categories.";
export const MSG_CATEGORY_CREATE_FAILED: string = "Failed to create category.";
export const MSG_CATEGORY_UPDATE_FAILED: string = "Failed to update category.";
export const MSG_CATEGORY_DELETE_FAILED: string = "Failed to delete category.";
export const MSG_CATEGORY_UNEXPECTED_ERROR: string = "An unexpected error occurred while processing category.";
export const MSG_CATEGORY_COMPCODE_MISSING: string = "Company code is missing";
export const MSG_CATEGORY_LOCCODE_MISSING: string = "Location code is missing";
export const MSG_CATEGORY_ENUSER_MISSING: string = "Username is missing";

// UI Elements
export const UI_CATEGORY_SEARCH_PLACEHOLDER: string = "Search by...";
export const UI_CATEGORY_DELETE_TITLE: string = "Confirm Delete";
export const UI_CATEGORY_DELETE_CONFIRMATION: string = "Are you sure you want to delete this category?";
export const UI_CATEGORY_DELETE_CONFIRMATION_NAMED: string = 'Are you sure you want to delete the category "{0}"?';
export const UI_CATEGORY_DELETING: string = "Deleting...";
export const UI_CATEGORY_LABEL_MAIN_CATEGORY: string = "Main Category";
export const UI_CATEGORY_ADD_BUTTON: string = "Add";
export const UI_CATEGORY_EDIT_ACTION: string = "Edit";
export const UI_CATEGORY_DELETE_ACTION: string = "Delete";

// ============================================================================
// Generic Name Management Messages - COMPLETE with all UI elements
// ============================================================================

// Page Titles
export const UI_GENERIC_VIEW_TITLE: string = "Generic Name";
export const UI_GENERIC_BREADCRUMB_VIEW: string = "Generic Name";
export const UI_GENERIC_TABLE_TITLE: string = "Generic Name Details";

// Success Messages
export const MSG_GENERIC_CREATE_SUCCESS: string = "Created Successfully!";
export const MSG_GENERIC_UPDATE_SUCCESS: string = "Updated Successfully!";
export const MSG_GENERIC_DELETE_SUCCESS: string = "Generic name deleted successfully";

// Warning Messages
export const MSG_GENERIC_ALREADY_EXISTS: string = "Generic Name already exists!";
export const MSG_GENERIC_REQUIRED_FIELDS: string = "Please Fill All the Required Fields";
export const MSG_GENERIC_NAME_REQUIRED: string = "Generic Name is required";
export const MSG_GENERIC_MAIN_CATEGORY_REQUIRED: string = "Main Category is required";
export const MSG_GENERIC_CATEGORY_REQUIRED: string = "Category is required";
export const MSG_GENERIC_SUBCATEGORY_REQUIRED: string = "Subcategory is required";
export const MSG_GENERIC_LEADING_SPACES: string = "Leading spaces are not allowed";
export const MSG_GENERIC_NO_ITEM_SELECTED: string = "No item selected";

// Error Messages
export const MSG_GENERIC_FETCH_INITIAL_FAILED: string = "Failed to load initial data";
export const MSG_GENERIC_CREATE_FAILED: string = "Failed to create Generic Name";
export const MSG_GENERIC_UPDATE_FAILED: string = "Failed to update Generic Name";
export const MSG_GENERIC_DELETE_FAILED: string = "Failed to delete";
export const MSG_GENERIC_DELETE_IN_USE: string = "Generic name cannot be deleted. It's already in use";
export const MSG_GENERIC_UNEXPECTED_ERROR: string = "An unexpected error occurred while processing Generic Name";
export const MSG_GENERIC_COMPCODE_MISSING: string = "Company code is missing";
export const MSG_GENERIC_LOCCODE_MISSING: string = "Location code is missing";
export const MSG_GENERIC_USERNAME_MISSING: string = "Username is missing";

// UI Elements
export const UI_GENERIC_SEARCH_PLACEHOLDER: string = "Search by...";
export const UI_GENERIC_DELETE_TITLE: string = "Confirm Delete";
export const UI_GENERIC_DELETE_CONFIRMATION: string = "Are you sure you want to delete the generic name?";
export const UI_GENERIC_DELETE_CONFIRMATION_NAMED: string = 'Are you sure you want to delete the generic name "{0}"?';
export const UI_GENERIC_DELETING: string = "Deleting...";
export const UI_GENERIC_ADD_BUTTON: string = "Add";
export const UI_GENERIC_EDIT_ACTION: string = "Edit";
export const UI_GENERIC_DELETE_ACTION: string = "Delete";

// Column Headers
export const UI_GENERIC_COL_ID: string = "Generic ID";
export const UI_GENERIC_COL_SUBCATEGORY: string = "Subcategory";
export const UI_GENERIC_COL_NAME: string = "Generic Name";
export const UI_GENERIC_COL_DESCRIPTION: string = "Description";

// ============================================================================
// Subcategory Management Messages - COMPLETE with all UI elements
// ============================================================================

// Page Titles
export const UI_SUBCATEGORY_VIEW_TITLE: string = "Subcategory";
export const UI_SUBCATEGORY_BREADCRUMB_VIEW: string = "Subcategory";
export const UI_SUBCATEGORY_TABLE_TITLE: string = "Subcategory";

// Success Messages
export const MSG_SUBCATEGORY_CREATE_SUCCESS: string = "Subcategory Created Successfully!";
export const MSG_SUBCATEGORY_UPDATE_SUCCESS: string = "Subcategory Updated Successfully!";
export const MSG_SUBCATEGORY_DELETE_SUCCESS: string = "Subcategory deleted successfully";

// Warning Messages
export const MSG_SUBCATEGORY_ALREADY_EXISTS: string = "Subcategory already exists under this Category!";
export const MSG_SUBCATEGORY_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_SUBCATEGORY_NAME_REQUIRED: string = "Subcategory Name is required";
export const MSG_SUBCATEGORY_MAIN_CATEGORY_REQUIRED: string = "Main Category is required";
export const MSG_SUBCATEGORY_CATEGORY_REQUIRED: string = "Category is required";
export const MSG_SUBCATEGORY_NO_SUBCATEGORY_SELECTED: string = "No subcategory selected";

// Error Messages
export const MSG_SUBCATEGORY_FETCH_FAILED: string = "Failed to fetch main categories.";
export const MSG_SUBCATEGORY_FETCH_CATEGORIES_FAILED: string = "Failed to fetch categories.";
export const MSG_SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED: string = "Failed to fetch subcategories.";
export const MSG_SUBCATEGORY_CREATE_FAILED: string = "Failed to create subcategory.";
export const MSG_SUBCATEGORY_UPDATE_FAILED: string = "Failed to update subcategory.";
export const MSG_SUBCATEGORY_DELETE_FAILED: string = "Failed to delete subcategory.";
export const MSG_SUBCATEGORY_DELETE_IN_USE: string = "This subcategory is used in generic names or items.";
export const MSG_SUBCATEGORY_UNEXPECTED_ERROR: string = "An unexpected error occurred while processing subcategory.";
export const MSG_SUBCATEGORY_COMPCODE_MISSING: string = "Company code is missing";
export const MSG_SUBCATEGORY_LOCCODE_MISSING: string = "Location code is missing";
export const MSG_SUBCATEGORY_ENUSER_MISSING: string = "Username is missing";

// UI Elements
export const UI_SUBCATEGORY_SEARCH_PLACEHOLDER: string = "Search by...";
export const UI_SUBCATEGORY_DELETE_TITLE: string = "Confirm Delete";
export const UI_SUBCATEGORY_DELETE_CONFIRMATION: string = "Are you sure you want to delete this subcategory?";
export const UI_SUBCATEGORY_DELETE_CONFIRMATION_NAMED: string = 'Are you sure you want to delete the subcategory "{0}"?';
export const UI_SUBCATEGORY_DELETING: string = "Deleting...";
export const UI_SUBCATEGORY_LABEL_MAIN_CATEGORY: string = "Main Category";
export const UI_SUBCATEGORY_LABEL_CATEGORY: string = "Category";
export const UI_SUBCATEGORY_ADD_BUTTON: string = "Add";
export const UI_SUBCATEGORY_EDIT_ACTION: string = "Edit";
export const UI_SUBCATEGORY_DELETE_ACTION: string = "Delete";

// Column Headers
export const UI_SUBCATEGORY_COL_CODE: string = "Code";
export const UI_SUBCATEGORY_COL_NAME: string = "Name";
export const UI_SUBCATEGORY_COL_DESCRIPTION: string = "Description";

// ============================================================================
// Document Type Management Messages
// ============================================================================

export const MSG_DOCTYPE_CREATE_SUCCESS: string = "Document Type Created Successfully!";
export const MSG_DOCTYPE_UPDATE_SUCCESS: string = "Document Type Updated Successfully!";
export const MSG_DOCTYPE_ALREADY_EXISTS: string = "Document type name already exists in this category.";
export const MSG_DOCTYPE_REQUIRED_FIELDS: string = "Please fill all required fields.";
export const MSG_DOCTYPE_NAME_REQUIRED: string = "Document Name is required";
export const MSG_DOCTYPE_CATEGORY_REQUIRED: string = "Document Category is required";
export const MSG_DOCTYPE_SAVE_CODE_REQUIRED: string = "Save Code is required";
export const MSG_DOCTYPE_FETCH_CATEGORIES_FAILED: string = "Failed to fetch document categories";
export const MSG_DOCTYPE_FETCH_TYPES_FAILED: string = "Failed to fetch document types";
export const MSG_DOCTYPE_FETCH_ITEMS_FAILED: string = "Failed to fetch items";
export const MSG_DOCTYPE_CREATE_FAILED: string = "Failed to create document type";
export const MSG_DOCTYPE_UPDATE_FAILED: string = "Failed to update document type";
export const MSG_DOCTYPE_UNEXPECTED_ERROR: string = "An unexpected error occurred.";
export const MSG_DOCTYPE_INPUT_MAX_LENGTH: string = "Input cannot exceed 30 characters.";
export const MSG_DOCTYPE_INPUT_EMPTY: string = "Input cannot be empty or just spaces.";

// ============================================================================
// Measuring Unit Management Messages
// ============================================================================

export const MSG_MEASURINGUNIT_CREATE_SUCCESS: string = "Created Successfully!";
export const MSG_MEASURINGUNIT_UPDATE_SUCCESS: string = "Updated Successfully!";
export const MSG_MEASURINGUNIT_DELETE_SUCCESS: string = 'Measuring Unit "{0}" deleted successfully';
export const MSG_MEASURINGUNIT_DELETE_FAILED: string = "Failed to delete measuring unit.";
export const MSG_MEASURINGUNIT_ALREADY_EXISTS: string = "Measuring Unit Already Exists.";
export const MSG_MEASURINGUNIT_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_MEASURINGUNIT_NAME_REQUIRED: string = "Measuring Unit Name is required.";
export const MSG_MEASURINGUNIT_UNEXPECTED_ERROR: string = "An unexpected error occurred.";
export const MSG_MEASURINGUNIT_FETCH_FAILED: string = "Failed to fetch measuring units.";
export const MSG_MEASURINGUNIT_INPUT_MAX_LENGTH: string = "Input cannot exceed 30 characters.";
export const MSG_MEASURINGUNIT_INPUT_EMPTY: string = "Input cannot be empty or just spaces.";
export const MSG_MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED: string = "Inline editing is not supported for measuring units";

// Error Messages
export const MSG_MEASURINGUNIT_COMPCODE_MISSING: string = "COMPCODE is required but missing";
export const MSG_MEASURINGUNIT_LOCCODE_MISSING: string = "LOCCODE is required but missing";
export const MSG_MEASURINGUNIT_CODE_MISSING: string = "Measuring unit code is required but missing";
export const MSG_MEASURINGUNIT_ENUSER_MISSING: string = "ENUSER is required but missing";
export const MSG_MEASURINGUNIT_DELETE_OPERATION_FAILED: string = "Delete operation failed";
export const MSG_MEASURINGUNIT_NULL_REFERENCE: string = "Server error: Null reference exception.";
export const MSG_MEASURINGUNIT_NULL_REFERENCE_DETAIL: string = "Check that all required fields match the backend expectations.";
export const MSG_MEASURINGUNIT_BAD_REQUEST: string = "Bad request. Invalid or missing parameters.";
export const MSG_MEASURINGUNIT_NOT_FOUND: string = "API endpoint not found or item doesn't exist.";
export const MSG_MEASURINGUNIT_METHOD_NOT_ALLOWED: string = "Method not allowed.";
export const MSG_MEASURINGUNIT_SERVER_ERROR: string = "Server error. Please try again.";
export const MSG_MEASURINGUNIT_SERVER_ERROR_PREFIX: string = "Server error:";
export const MSG_MEASURINGUNIT_NO_RESPONSE: string = "No response from server. Check network connection.";

// UI Elements
export const UI_MEASURING_UNIT_VIEW_TITLE: string = "Measuring Unit";
export const UI_MEASURING_UNIT_BREADCRUMB_VIEW: string = "Measuring Unit";
export const UI_MEASURING_UNIT_SEARCH_PLACEHOLDER: string = "Search by...";
export const UI_MEASURING_UNIT_DELETE_TITLE: string = "Confirm Delete";
export const UI_MEASURING_UNIT_DELETE_CONFIRMATION: string = "Are you sure you want to delete this measuring unit?";
export const UI_MEASURING_UNIT_DELETE_CONFIRMATION_NAMED: string = 'Are you sure you want to delete the measuring unit "{0}"?';
export const UI_MEASURING_UNIT_DELETING: string = "Deleting...";

// ============================================================================
// Package Management Messages
// ============================================================================

export const MSG_PACKAGE_CREATE_SUCCESS: string = "Created Successfully!";
export const MSG_PACKAGE_UPDATE_SUCCESS: string = "Updated Successfully!";
export const MSG_PACKAGE_DELETE_SUCCESS: string = 'Package deleted successfully!';
export const MSG_PACKAGE_DELETE_FAILED: string = "Failed to delete package.";
export const MSG_PACKAGE_ALREADY_EXISTS: string = "Package Already Exists!";
export const MSG_PACKAGE_REQUIRED_FIELDS: string = "Please Fill All the Required Fields.";
export const MSG_PACKAGE_NAME_REQUIRED: string = "Package Name is required";
export const MSG_PACKAGE_UNEXPECTED_ERROR: string = "An unexpected error occurred.";
export const MSG_PACKAGE_FETCH_FAILED: string = "Failed to fetch packages.";
export const MSG_NO_PACKAGE_SELECTED: string = "No package selected";

// ============================================================================
// UI Text Messages (Shared across all modules)
// ============================================================================

export const UI_BREADCRUMB_HOME: string = "Home";
export const UI_BREADCRUMB_SETTINGS: string = "Settings";
export const UI_BREADCRUMB_MEASURING_UNIT: string = "Measuring unit";
export const UI_BREADCRUMB_PACKAGE: string = "Package";
export const UI_PAGE_TITLE_CREATE: string = "Create Measuring Units";
export const UI_PAGE_TITLE_EDIT: string = "Edit Measuring Units";
export const UI_PAGE_TITLE_CREATE_PACKAGE: string = "Create Packages";
export const UI_PAGE_TITLE_EDIT_PACKAGE: string = "Edit Packages";
export const UI_BUTTON_CLEAR: string = "Clear";
export const UI_BUTTON_CONFIRM: string = "Confirm";
export const UI_BUTTON_MODIFY: string = "Modify";
export const UI_BUTTON_CANCEL: string = "Cancel";
export const UI_BUTTON_DELETE: string = "Delete";
export const UI_BUTTON_ADD: string = "Add";
export const UI_TOOLTIP_BACK: string = "Back to All Items";
export const UI_LABEL_MEASURING_UNIT: string = "Measuring Unit";
export const UI_LABEL_DESCRIPTION: string = "Description";
export const UI_LABEL_PACKAGE_NAME: string = "Package Name";

// Action Texts (Shared)
export const UI_ACTION_EDIT: string = "Edit";
export const UI_ACTION_DELETE: string = "Delete";

// ============================================================================
// Validation Input Messages
// ============================================================================

/**
 * Returns a message for input max length validation
 * @param maxLength - Maximum allowed characters
 * @returns Formatted validation message
 */
export const getInputMaxLengthMessage = (maxLength: number): string => 
  `Input cannot exceed ${maxLength} characters.`;

/**
 * Returns a field-specific max length validation message
 * @param fieldName - Name of the field
 * @param maxLength - Maximum allowed characters
 * @returns Formatted validation message
 */
export const getFieldMaxLengthMessage = (fieldName: string, maxLength: number): string => 
  `${fieldName} cannot exceed ${maxLength} characters.`;

// ============================================================================
// Message Categories and Types for Better Organization
// ============================================================================

// Message type categories
export enum MessageCategory {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

// Message interface for structured messages
export interface AppMessage {
  text: string;
  category: MessageCategory;
  code?: string;
}

// Message codes for i18n or tracking
export enum MessageCode {
  // GRN Messages
  GRN_CREATE_SUCCESS = 'GRN_CREATE_SUCCESS',
  GRN_CREATE_FAILED = 'GRN_CREATE_FAILED',
  GRN_SAVE_SUCCESS = 'GRN_SAVE_SUCCESS',
  GRN_SAVE_FAILED = 'GRN_SAVE_FAILED',
  GRN_HOLD_SUCCESS = 'GRN_HOLD_SUCCESS',
  GRN_HOLD_FAILED = 'GRN_HOLD_FAILED',
  GRN_UPDATE_SUCCESS = 'GRN_UPDATE_SUCCESS',
  GRN_UPDATE_FAILED = 'GRN_UPDATE_FAILED',
  GRN_DELETE_SUCCESS = 'GRN_DELETE_SUCCESS',
  GRN_DELETE_FAILED = 'GRN_DELETE_FAILED',
  
  // Authentication Messages
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  
  // Migration Messages
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  TEMPLATE_DOWNLOADED = 'TEMPLATE_DOWNLOADED',
  FILE_SIZE_EXCEEDED = 'FILE_SIZE_EXCEEDED',
  ITEMS_IMPORTED = 'ITEMS_IMPORTED',
  MANDATORY_MAPPING = 'MANDATORY_MAPPING',
  SKU_DUPLICATE = 'SKU_DUPLICATE',
  TEMPLATE_MISMATCH = 'TEMPLATE_MISMATCH',
  PARSING_EXCEL_ERROR = 'PARSING_EXCEL_ERROR',
  MERGED_CELLS_ERROR = 'MERGED_CELLS_ERROR',
  INVALID_START_POINT = 'INVALID_START_POINT',
  NO_DATA_ERROR = 'NO_DATA_ERROR',
  FAILED_TO_FETCH_DATA_ERROR = 'FAILED_TO_FETCH_DATA_ERROR',
  
  // Item Messages
  ITEMS_LOAD_FAILED = 'ITEMS_LOAD_FAILED',
  ITEM_DELETE_SUCCESS = 'ITEM_DELETE_SUCCESS',
  ITEM_DELETE_FAILED = 'ITEM_DELETE_FAILED',
  ITEM_DELETE_OPERATION_FAILED = 'ITEM_DELETE_OPERATION_FAILED',
  ITEM_DELETE_NO_RESPONSE = 'ITEM_DELETE_NO_RESPONSE',
  ITEM_DELETE_UNKNOWN_ERROR = 'ITEM_DELETE_UNKNOWN_ERROR',
  
  // Export Messages
  EXPORT_NO_DATA = 'EXPORT_NO_DATA',
  EXPORT_SUCCESS = 'EXPORT_SUCCESS',
  EXPORT_FAILED = 'EXPORT_FAILED',
  EXPORT_SELECT_FIELD = 'EXPORT_SELECT_FIELD',
  
  // Item Create/Edit Messages
  VALIDATION_REQUIRED_FIELDS = 'VALIDATION_REQUIRED_FIELDS',
  NAVIGATION_ERROR = 'NAVIGATION_ERROR',
  
  // Brand Messages
  BRAND_CREATE_SUCCESS = 'BRAND_CREATE_SUCCESS',
  BRAND_UPDATE_SUCCESS = 'BRAND_UPDATE_SUCCESS',
  BRAND_DELETE_SUCCESS = 'BRAND_DELETE_SUCCESS',
  BRAND_DELETE_FAILED = 'BRAND_DELETE_FAILED',
  BRAND_DELETE_IN_USE = 'BRAND_DELETE_IN_USE',
  BRAND_ALREADY_EXISTS = 'BRAND_ALREADY_EXISTS',
  BRAND_REQUIRED_FIELDS = 'BRAND_REQUIRED_FIELDS',
  BRAND_NAME_REQUIRED = 'BRAND_NAME_REQUIRED',
  BRAND_UNEXPECTED_ERROR = 'BRAND_UNEXPECTED_ERROR',
  BRAND_FETCH_FAILED = 'BRAND_FETCH_FAILED',
  BRAND_SEARCH_PLACEHOLDER = 'BRAND_SEARCH_PLACEHOLDER',
  BRAND_INLINE_EDIT_NOT_SUPPORTED = 'BRAND_INLINE_EDIT_NOT_SUPPORTED',
  BRAND_CONFIRM_DELETE = 'BRAND_CONFIRM_DELETE',
  BRAND_CONFIRM_DELETE_NAMED = 'BRAND_CONFIRM_DELETE_NAMED',

  // Main Category Messages - COMPLETE
  MAINCAT_VIEW_TITLE = 'MAINCAT_VIEW_TITLE',
  MAINCAT_BREADCRUMB_VIEW = 'MAINCAT_BREADCRUMB_VIEW',
  MAINCAT_TABLE_TITLE = 'MAINCAT_TABLE_TITLE',
  MAINCAT_CREATE_SUCCESS = 'MAINCAT_CREATE_SUCCESS',
  MAINCAT_UPDATE_SUCCESS = 'MAINCAT_UPDATE_SUCCESS',
  MAINCAT_DELETE_SUCCESS = 'MAINCAT_DELETE_SUCCESS',
  MAINCAT_ALREADY_EXISTS = 'MAINCAT_ALREADY_EXISTS',
  MAINCAT_REQUIRED_FIELDS = 'MAINCAT_REQUIRED_FIELDS',
  MAINCAT_NAME_REQUIRED = 'MAINCAT_NAME_REQUIRED',
  MAINCAT_NO_CATEGORY_SELECTED = 'MAINCAT_NO_CATEGORY_SELECTED',
  MAINCAT_FETCH_FAILED = 'MAINCAT_FETCH_FAILED',
  MAINCAT_DELETE_FAILED = 'MAINCAT_DELETE_FAILED',
  MAINCAT_UNEXPECTED_ERROR = 'MAINCAT_UNEXPECTED_ERROR',
  MAINCAT_COMPCODE_MISSING = 'MAINCAT_COMPCODE_MISSING',
  MAINCAT_LOCCODE_MISSING = 'MAINCAT_LOCCODE_MISSING',
  MAINCAT_USERNAME_MISSING = 'MAINCAT_USERNAME_MISSING',
  MAINCAT_SEARCH_PLACEHOLDER = 'MAINCAT_SEARCH_PLACEHOLDER',
  MAINCAT_DELETE_TITLE = 'MAINCAT_DELETE_TITLE',
  MAINCAT_DELETE_CONFIRMATION = 'MAINCAT_DELETE_CONFIRMATION',
  MAINCAT_DELETE_CONFIRMATION_NAMED = 'MAINCAT_DELETE_CONFIRMATION_NAMED',
  MAINCAT_DELETING = 'MAINCAT_DELETING',
  MAINCAT_ADD_BUTTON = 'MAINCAT_ADD_BUTTON',
  MAINCAT_EDIT_ACTION = 'MAINCAT_EDIT_ACTION',
  MAINCAT_DELETE_ACTION = 'MAINCAT_DELETE_ACTION',
  MAINCAT_COL_CODE = 'MAINCAT_COL_CODE',
  MAINCAT_COL_NAME = 'MAINCAT_COL_NAME',
  MAINCAT_COL_DESCRIPTION = 'MAINCAT_COL_DESCRIPTION',
  
  // Category Messages - COMPLETE
  CATEGORY_VIEW_TITLE = 'CATEGORY_VIEW_TITLE',
  CATEGORY_BREADCRUMB_VIEW = 'CATEGORY_BREADCRUMB_VIEW',
  CATEGORY_TABLE_TITLE = 'CATEGORY_TABLE_TITLE',
  CATEGORY_CREATE_SUCCESS = 'CATEGORY_CREATE_SUCCESS',
  CATEGORY_UPDATE_SUCCESS = 'CATEGORY_UPDATE_SUCCESS',
  CATEGORY_DELETE_SUCCESS = 'CATEGORY_DELETE_SUCCESS',
  CATEGORY_DELETE_FAILED = 'CATEGORY_DELETE_FAILED',
  CATEGORY_ALREADY_EXISTS = 'CATEGORY_ALREADY_EXISTS',
  CATEGORY_REQUIRED_FIELDS = 'CATEGORY_REQUIRED_FIELDS',
  CATEGORY_NAME_REQUIRED = 'CATEGORY_NAME_REQUIRED',
  CATEGORY_MAIN_CATEGORY_REQUIRED = 'CATEGORY_MAIN_CATEGORY_REQUIRED',
  CATEGORY_FETCH_FAILED = 'CATEGORY_FETCH_FAILED',
  CATEGORY_FETCH_CATEGORIES_FAILED = 'CATEGORY_FETCH_CATEGORIES_FAILED',
  CATEGORY_CREATE_FAILED = 'CATEGORY_CREATE_FAILED',
  CATEGORY_UPDATE_FAILED = 'CATEGORY_UPDATE_FAILED',
  CATEGORY_UNEXPECTED_ERROR = 'CATEGORY_UNEXPECTED_ERROR',
  CATEGORY_NO_CATEGORY_SELECTED = 'CATEGORY_NO_CATEGORY_SELECTED',
  CATEGORY_COMPCODE_MISSING = 'CATEGORY_COMPCODE_MISSING',
  CATEGORY_LOCCODE_MISSING = 'CATEGORY_LOCCODE_MISSING',
  CATEGORY_ENUSER_MISSING = 'CATEGORY_ENUSER_MISSING',
  CATEGORY_SEARCH_PLACEHOLDER = 'CATEGORY_SEARCH_PLACEHOLDER',
  CATEGORY_DELETE_TITLE = 'CATEGORY_DELETE_TITLE',
  CATEGORY_DELETE_CONFIRMATION = 'CATEGORY_DELETE_CONFIRMATION',
  CATEGORY_DELETE_CONFIRMATION_NAMED = 'CATEGORY_DELETE_CONFIRMATION_NAMED',
  CATEGORY_DELETING = 'CATEGORY_DELETING',
  CATEGORY_LABEL_MAIN_CATEGORY = 'CATEGORY_LABEL_MAIN_CATEGORY',
  CATEGORY_ADD_BUTTON = 'CATEGORY_ADD_BUTTON',
  CATEGORY_EDIT_ACTION = 'CATEGORY_EDIT_ACTION',
  CATEGORY_DELETE_ACTION = 'CATEGORY_DELETE_ACTION',
  
  // Generic Name Messages - COMPLETE
  GENERIC_VIEW_TITLE = 'GENERIC_VIEW_TITLE',
  GENERIC_BREADCRUMB_VIEW = 'GENERIC_BREADCRUMB_VIEW',
  GENERIC_TABLE_TITLE = 'GENERIC_TABLE_TITLE',
  GENERIC_CREATE_SUCCESS = 'GENERIC_CREATE_SUCCESS',
  GENERIC_UPDATE_SUCCESS = 'GENERIC_UPDATE_SUCCESS',
  GENERIC_DELETE_SUCCESS = 'GENERIC_DELETE_SUCCESS',
  GENERIC_ALREADY_EXISTS = 'GENERIC_ALREADY_EXISTS',
  GENERIC_REQUIRED_FIELDS = 'GENERIC_REQUIRED_FIELDS',
  GENERIC_NAME_REQUIRED = 'GENERIC_NAME_REQUIRED',
  GENERIC_MAIN_CATEGORY_REQUIRED = 'GENERIC_MAIN_CATEGORY_REQUIRED',
  GENERIC_CATEGORY_REQUIRED = 'GENERIC_CATEGORY_REQUIRED',
  GENERIC_SUBCATEGORY_REQUIRED = 'GENERIC_SUBCATEGORY_REQUIRED',
  GENERIC_LEADING_SPACES = 'GENERIC_LEADING_SPACES',
  GENERIC_FETCH_INITIAL_FAILED = 'GENERIC_FETCH_INITIAL_FAILED',
  GENERIC_CREATE_FAILED = 'GENERIC_CREATE_FAILED',
  GENERIC_UPDATE_FAILED = 'GENERIC_UPDATE_FAILED',
  GENERIC_DELETE_FAILED = 'GENERIC_DELETE_FAILED',
  GENERIC_DELETE_IN_USE = 'GENERIC_DELETE_IN_USE',
  GENERIC_UNEXPECTED_ERROR = 'GENERIC_UNEXPECTED_ERROR',
  GENERIC_NO_ITEM_SELECTED = 'GENERIC_NO_ITEM_SELECTED',
  GENERIC_COMPCODE_MISSING = 'GENERIC_COMPCODE_MISSING',
  GENERIC_LOCCODE_MISSING = 'GENERIC_LOCCODE_MISSING',
  GENERIC_USERNAME_MISSING = 'GENERIC_USERNAME_MISSING',
  GENERIC_SEARCH_PLACEHOLDER = 'GENERIC_SEARCH_PLACEHOLDER',
  GENERIC_DELETE_TITLE = 'GENERIC_DELETE_TITLE',
  GENERIC_DELETE_CONFIRMATION = 'GENERIC_DELETE_CONFIRMATION',
  GENERIC_DELETE_CONFIRMATION_NAMED = 'GENERIC_DELETE_CONFIRMATION_NAMED',
  GENERIC_DELETING = 'GENERIC_DELETING',
  GENERIC_ADD_BUTTON = 'GENERIC_ADD_BUTTON',
  GENERIC_EDIT_ACTION = 'GENERIC_EDIT_ACTION',
  GENERIC_DELETE_ACTION = 'GENERIC_DELETE_ACTION',
  GENERIC_COL_ID = 'GENERIC_COL_ID',
  GENERIC_COL_SUBCATEGORY = 'GENERIC_COL_SUBCATEGORY',
  GENERIC_COL_NAME = 'GENERIC_COL_NAME',
  GENERIC_COL_DESCRIPTION = 'GENERIC_COL_DESCRIPTION',
  
  // Subcategory Messages
  SUBCATEGORY_VIEW_TITLE = 'SUBCATEGORY_VIEW_TITLE',
  SUBCATEGORY_BREADCRUMB_VIEW = 'SUBCATEGORY_BREADCRUMB_VIEW',
  SUBCATEGORY_TABLE_TITLE = 'SUBCATEGORY_TABLE_TITLE',
  SUBCATEGORY_CREATE_SUCCESS = 'SUBCATEGORY_CREATE_SUCCESS',
  SUBCATEGORY_UPDATE_SUCCESS = 'SUBCATEGORY_UPDATE_SUCCESS',
  SUBCATEGORY_DELETE_SUCCESS = 'SUBCATEGORY_DELETE_SUCCESS',
  SUBCATEGORY_DELETE_FAILED = 'SUBCATEGORY_DELETE_FAILED',
  SUBCATEGORY_DELETE_IN_USE = 'SUBCATEGORY_DELETE_IN_USE',
  SUBCATEGORY_ALREADY_EXISTS = 'SUBCATEGORY_ALREADY_EXISTS',
  SUBCATEGORY_REQUIRED_FIELDS = 'SUBCATEGORY_REQUIRED_FIELDS',
  SUBCATEGORY_NAME_REQUIRED = 'SUBCATEGORY_NAME_REQUIRED',
  SUBCATEGORY_MAIN_CATEGORY_REQUIRED = 'SUBCATEGORY_MAIN_CATEGORY_REQUIRED',
  SUBCATEGORY_CATEGORY_REQUIRED = 'SUBCATEGORY_CATEGORY_REQUIRED',
  SUBCATEGORY_FETCH_FAILED = 'SUBCATEGORY_FETCH_FAILED',
  SUBCATEGORY_FETCH_CATEGORIES_FAILED = 'SUBCATEGORY_FETCH_CATEGORIES_FAILED',
  SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED = 'SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED',
  SUBCATEGORY_CREATE_FAILED = 'SUBCATEGORY_CREATE_FAILED',
  SUBCATEGORY_UPDATE_FAILED = 'SUBCATEGORY_UPDATE_FAILED',
  SUBCATEGORY_UNEXPECTED_ERROR = 'SUBCATEGORY_UNEXPECTED_ERROR',
  SUBCATEGORY_NO_SUBCATEGORY_SELECTED = 'SUBCATEGORY_NO_SUBCATEGORY_SELECTED',
  SUBCATEGORY_COMPCODE_MISSING = 'SUBCATEGORY_COMPCODE_MISSING',
  SUBCATEGORY_LOCCODE_MISSING = 'SUBCATEGORY_LOCCODE_MISSING',
  SUBCATEGORY_ENUSER_MISSING = 'SUBCATEGORY_ENUSER_MISSING',
  SUBCATEGORY_SEARCH_PLACEHOLDER = 'SUBCATEGORY_SEARCH_PLACEHOLDER',
  SUBCATEGORY_DELETE_TITLE = 'SUBCATEGORY_DELETE_TITLE',
  SUBCATEGORY_DELETE_CONFIRMATION = 'SUBCATEGORY_DELETE_CONFIRMATION',
  SUBCATEGORY_DELETE_CONFIRMATION_NAMED = 'SUBCATEGORY_DELETE_CONFIRMATION_NAMED',
  SUBCATEGORY_DELETING = 'SUBCATEGORY_DELETING',
  SUBCATEGORY_LABEL_MAIN_CATEGORY = 'SUBCATEGORY_LABEL_MAIN_CATEGORY',
  SUBCATEGORY_LABEL_CATEGORY = 'SUBCATEGORY_LABEL_CATEGORY',
  SUBCATEGORY_ADD_BUTTON = 'SUBCATEGORY_ADD_BUTTON',
  SUBCATEGORY_EDIT_ACTION = 'SUBCATEGORY_EDIT_ACTION',
  SUBCATEGORY_DELETE_ACTION = 'SUBCATEGORY_DELETE_ACTION',
  SUBCATEGORY_COL_CODE = 'SUBCATEGORY_COL_CODE',
  SUBCATEGORY_COL_NAME = 'SUBCATEGORY_COL_NAME',
  SUBCATEGORY_COL_DESCRIPTION = 'SUBCATEGORY_COL_DESCRIPTION',
  
  // Document Type Messages
  DOCTYPE_CREATE_SUCCESS = 'DOCTYPE_CREATE_SUCCESS',
  DOCTYPE_UPDATE_SUCCESS = 'DOCTYPE_UPDATE_SUCCESS',
  DOCTYPE_ALREADY_EXISTS = 'DOCTYPE_ALREADY_EXISTS',
  DOCTYPE_REQUIRED_FIELDS = 'DOCTYPE_REQUIRED_FIELDS',
  DOCTYPE_NAME_REQUIRED = 'DOCTYPE_NAME_REQUIRED',
  DOCTYPE_CATEGORY_REQUIRED = 'DOCTYPE_CATEGORY_REQUIRED',
  DOCTYPE_SAVE_CODE_REQUIRED = 'DOCTYPE_SAVE_CODE_REQUIRED',
  DOCTYPE_FETCH_CATEGORIES_FAILED = 'DOCTYPE_FETCH_CATEGORIES_FAILED',
  DOCTYPE_FETCH_TYPES_FAILED = 'DOCTYPE_FETCH_TYPES_FAILED',
  DOCTYPE_FETCH_ITEMS_FAILED = 'DOCTYPE_FETCH_ITEMS_FAILED',
  DOCTYPE_CREATE_FAILED = 'DOCTYPE_CREATE_FAILED',
  DOCTYPE_UPDATE_FAILED = 'DOCTYPE_UPDATE_FAILED',
  DOCTYPE_UNEXPECTED_ERROR = 'DOCTYPE_UNEXPECTED_ERROR',
  DOCTYPE_INPUT_MAX_LENGTH = 'DOCTYPE_INPUT_MAX_LENGTH',
  DOCTYPE_INPUT_EMPTY = 'DOCTYPE_INPUT_EMPTY',
  
  // Measuring Unit Messages
  MEASURINGUNIT_CREATE_SUCCESS = 'MEASURINGUNIT_CREATE_SUCCESS',
  MEASURINGUNIT_UPDATE_SUCCESS = 'MEASURINGUNIT_UPDATE_SUCCESS',
  MEASURINGUNIT_DELETE_SUCCESS = 'MEASURINGUNIT_DELETE_SUCCESS',
  MEASURINGUNIT_DELETE_FAILED = 'MEASURINGUNIT_DELETE_FAILED',
  MEASURINGUNIT_ALREADY_EXISTS = 'MEASURINGUNIT_ALREADY_EXISTS',
  MEASURINGUNIT_REQUIRED_FIELDS = 'MEASURINGUNIT_REQUIRED_FIELDS',
  MEASURINGUNIT_NAME_REQUIRED = 'MEASURINGUNIT_NAME_REQUIRED',
  MEASURINGUNIT_UNEXPECTED_ERROR = 'MEASURINGUNIT_UNEXPECTED_ERROR',
  MEASURINGUNIT_FETCH_FAILED = 'MEASURINGUNIT_FETCH_FAILED',
  MEASURINGUNIT_INPUT_MAX_LENGTH = 'MEASURINGUNIT_INPUT_MAX_LENGTH',
  MEASURINGUNIT_INPUT_EMPTY = 'MEASURINGUNIT_INPUT_EMPTY',
  MEASURINGUNIT_COMPCODE_MISSING = 'MEASURINGUNIT_COMPCODE_MISSING',
  MEASURINGUNIT_LOCCODE_MISSING = 'MEASURINGUNIT_LOCCODE_MISSING',
  MEASURINGUNIT_CODE_MISSING = 'MEASURINGUNIT_CODE_MISSING',
  MEASURINGUNIT_ENUSER_MISSING = 'MEASURINGUNIT_ENUSER_MISSING',
  MEASURINGUNIT_DELETE_OPERATION_FAILED = 'MEASURINGUNIT_DELETE_OPERATION_FAILED',
  MEASURINGUNIT_NULL_REFERENCE = 'MEASURINGUNIT_NULL_REFERENCE',
  MEASURINGUNIT_NULL_REFERENCE_DETAIL = 'MEASURINGUNIT_NULL_REFERENCE_DETAIL',
  MEASURINGUNIT_BAD_REQUEST = 'MEASURINGUNIT_BAD_REQUEST',
  MEASURINGUNIT_NOT_FOUND = 'MEASURINGUNIT_NOT_FOUND',
  MEASURINGUNIT_METHOD_NOT_ALLOWED = 'MEASURINGUNIT_METHOD_NOT_ALLOWED',
  MEASURINGUNIT_SERVER_ERROR = 'MEASURINGUNIT_SERVER_ERROR',
  MEASURINGUNIT_SERVER_ERROR_PREFIX = 'MEASURINGUNIT_SERVER_ERROR_PREFIX',
  MEASURINGUNIT_NO_RESPONSE = 'MEASURINGUNIT_NO_RESPONSE',
  MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED = 'MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED',
  MEASURINGUNIT_SEARCH_PLACEHOLDER = 'MEASURINGUNIT_SEARCH_PLACEHOLDER',
  MEASURINGUNIT_DELETE_TITLE = 'MEASURINGUNIT_DELETE_TITLE',
  MEASURINGUNIT_DELETE_CONFIRMATION = 'MEASURINGUNIT_DELETE_CONFIRMATION',
  MEASURINGUNIT_DELETE_CONFIRMATION_NAMED = 'MEASURINGUNIT_DELETE_CONFIRMATION_NAMED',
  MEASURINGUNIT_DELETING = 'MEASURINGUNIT_DELETING',
  MEASURINGUNIT_VIEW_TITLE = 'MEASURINGUNIT_VIEW_TITLE',
  MEASURINGUNIT_BREADCRUMB_VIEW = 'MEASURINGUNIT_BREADCRUMB_VIEW',
  
  // Package Messages
  PACKAGE_CREATE_SUCCESS = 'PACKAGE_CREATE_SUCCESS',
  PACKAGE_UPDATE_SUCCESS = 'PACKAGE_UPDATE_SUCCESS',
  PACKAGE_DELETE_SUCCESS = 'PACKAGE_DELETE_SUCCESS',
  PACKAGE_DELETE_FAILED = 'PACKAGE_DELETE_FAILED',
  PACKAGE_ALREADY_EXISTS = 'PACKAGE_ALREADY_EXISTS',
  PACKAGE_REQUIRED_FIELDS = 'PACKAGE_REQUIRED_FIELDS',
  PACKAGE_NAME_REQUIRED = 'PACKAGE_NAME_REQUIRED',
  PACKAGE_UNEXPECTED_ERROR = 'PACKAGE_UNEXPECTED_ERROR',
  PACKAGE_FETCH_FAILED = 'PACKAGE_FETCH_FAILED',
  NO_PACKAGE_SELECTED = 'NO_PACKAGE_SELECTED',
  
  // UI Text Messages (Shared)
  UI_BREADCRUMB_HOME = 'UI_BREADCRUMB_HOME',
  UI_BREADCRUMB_SETTINGS = 'UI_BREADCRUMB_SETTINGS',
  UI_BREADCRUMB_MEASURING_UNIT = 'UI_BREADCRUMB_MEASURING_UNIT',
  UI_BREADCRUMB_PACKAGE = 'UI_BREADCRUMB_PACKAGE',
  UI_PAGE_TITLE_CREATE = 'UI_PAGE_TITLE_CREATE',
  UI_PAGE_TITLE_EDIT = 'UI_PAGE_TITLE_EDIT',
  UI_PAGE_TITLE_CREATE_PACKAGE = 'UI_PAGE_TITLE_CREATE_PACKAGE',
  UI_PAGE_TITLE_EDIT_PACKAGE = 'UI_PAGE_TITLE_EDIT_PACKAGE',
  UI_BUTTON_CLEAR = 'UI_BUTTON_CLEAR',
  UI_BUTTON_CONFIRM = 'UI_BUTTON_CONFIRM',
  UI_BUTTON_MODIFY = 'UI_BUTTON_MODIFY',
  UI_BUTTON_CANCEL = 'UI_BUTTON_CANCEL',
  UI_BUTTON_DELETE = 'UI_BUTTON_DELETE',
  UI_BUTTON_ADD = 'UI_BUTTON_ADD',
  UI_TOOLTIP_BACK = 'UI_TOOLTIP_BACK',
  UI_LABEL_MEASURING_UNIT = 'UI_LABEL_MEASURING_UNIT',
  UI_LABEL_DESCRIPTION = 'UI_LABEL_DESCRIPTION',
  UI_LABEL_PACKAGE_NAME = 'UI_LABEL_PACKAGE_NAME',
  UI_ACTION_EDIT = 'UI_ACTION_EDIT',
  UI_ACTION_DELETE = 'UI_ACTION_DELETE',
}

// ============================================================================
// Structured Messages with Codes
// ============================================================================

export const STRUCTURED_MESSAGES: Record<MessageCode, AppMessage> = {
  // GRN Messages
  [MessageCode.GRN_CREATE_SUCCESS]: {
    text: MSG_GRN_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GRN_CREATE_SUCCESS,
  },
  [MessageCode.GRN_CREATE_FAILED]: {
    text: MSG_GRN_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GRN_CREATE_FAILED,
  },
  [MessageCode.GRN_SAVE_SUCCESS]: {
    text: MSG_GRN_SAVE,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GRN_SAVE_SUCCESS,
  },
  [MessageCode.GRN_SAVE_FAILED]: {
    text: MSG_GRN_SAVE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GRN_SAVE_FAILED,
  },
  [MessageCode.GRN_HOLD_SUCCESS]: {
    text: MSG_GRN_HOLD,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GRN_HOLD_SUCCESS,
  },
  [MessageCode.GRN_HOLD_FAILED]: {
    text: MSG_GRN_HOLD_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GRN_HOLD_FAILED,
  },
  [MessageCode.GRN_UPDATE_SUCCESS]: {
    text: MSG_GRN_UPDATE,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GRN_UPDATE_SUCCESS,
  },
  [MessageCode.GRN_UPDATE_FAILED]: {
    text: MSG_GRN_UPDATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GRN_UPDATE_FAILED,
  },
  [MessageCode.GRN_DELETE_SUCCESS]: {
    text: MSG_GRN_DELETE,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GRN_DELETE_SUCCESS,
  },
  [MessageCode.GRN_DELETE_FAILED]: {
    text: MSG_GRN_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GRN_DELETE_FAILED,
  },
  
  // Authentication Messages
  [MessageCode.LOGIN_SUCCESS]: {
    text: LOGIN_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.LOGIN_SUCCESS,
  },
  [MessageCode.LOGIN_FAILED]: {
    text: LOGIN_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.LOGIN_FAILED,
  },
  
  // Migration Messages
  [MessageCode.INVALID_FILE_TYPE]: {
    text: MSG_INVALID_FILE_TYPE,
    category: MessageCategory.ERROR,
    code: MessageCode.INVALID_FILE_TYPE,
  },
  [MessageCode.TEMPLATE_DOWNLOADED]: {
    text: MSG_TEMPLATE_DOWNLOADED,
    category: MessageCategory.SUCCESS,
    code: MessageCode.TEMPLATE_DOWNLOADED,
  },
  [MessageCode.FILE_SIZE_EXCEEDED]: {
    text: getMsgFileSizeExceeded(10),
    category: MessageCategory.ERROR,
    code: MessageCode.FILE_SIZE_EXCEEDED,
  },
  [MessageCode.ITEMS_IMPORTED]: {
    text: getSuccessItemsImported(0),
    category: MessageCategory.SUCCESS,
    code: MessageCode.ITEMS_IMPORTED,
  },
  [MessageCode.MANDATORY_MAPPING]: {
    text: VALIDATION_MANDATORY_MAPPING,
    category: MessageCategory.WARNING,
    code: MessageCode.MANDATORY_MAPPING,
  },
  [MessageCode.SKU_DUPLICATE]: {
    text: VALIDATION_SKU_DUPLICATE,
    category: MessageCategory.ERROR,
    code: MessageCode.SKU_DUPLICATE,
  },
  [MessageCode.TEMPLATE_MISMATCH]: {
    text: VALIDATION_TEMPLATE_MISMATCH,
    category: MessageCategory.ERROR,
    code: MessageCode.TEMPLATE_MISMATCH,
  },
  [MessageCode.PARSING_EXCEL_ERROR]: {
    text: ERROR_PARSING_EXCEL,
    category: MessageCategory.ERROR,
    code: MessageCode.PARSING_EXCEL_ERROR,
  },
  [MessageCode.MERGED_CELLS_ERROR]: {
    text: ERROR_MERGED_CELLS,
    category: MessageCategory.ERROR,
    code: MessageCode.MERGED_CELLS_ERROR,
  },
  [MessageCode.INVALID_START_POINT]: {
    text: ERROR_INVALID_START_POINT,
    category: MessageCategory.ERROR,
    code: MessageCode.INVALID_START_POINT,
  },
  [MessageCode.NO_DATA_ERROR]: {
    text: ERROR_NO_DATA,
    category: MessageCategory.ERROR,
    code: MessageCode.NO_DATA_ERROR,
  },
  [MessageCode.FAILED_TO_FETCH_DATA_ERROR]: {
    text: ERROR_FAILED_TO_FETCH_DATA,
    category: MessageCategory.ERROR,
    code: MessageCode.FAILED_TO_FETCH_DATA_ERROR,
  },
  
  // Item Messages
  [MessageCode.ITEMS_LOAD_FAILED]: {
    text: MSG_ITEMS_LOAD_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.ITEMS_LOAD_FAILED,
  },
  [MessageCode.ITEM_DELETE_SUCCESS]: {
    text: MSG_ITEM_DELETE_SUCCESS(''),
    category: MessageCategory.SUCCESS,
    code: MessageCode.ITEM_DELETE_SUCCESS,
  },
  [MessageCode.ITEM_DELETE_FAILED]: {
    text: MSG_ITEM_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.ITEM_DELETE_FAILED,
  },
  [MessageCode.ITEM_DELETE_OPERATION_FAILED]: {
    text: MSG_ITEM_DELETE_OPERATION_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.ITEM_DELETE_OPERATION_FAILED,
  },
  [MessageCode.ITEM_DELETE_NO_RESPONSE]: {
    text: MSG_ITEM_DELETE_NO_RESPONSE,
    category: MessageCategory.ERROR,
    code: MessageCode.ITEM_DELETE_NO_RESPONSE,
  },
  [MessageCode.ITEM_DELETE_UNKNOWN_ERROR]: {
    text: MSG_ITEM_DELETE_UNKNOWN_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.ITEM_DELETE_UNKNOWN_ERROR,
  },
  
  // Export Messages
  [MessageCode.EXPORT_NO_DATA]: {
    text: MSG_EXPORT_NO_DATA,
    category: MessageCategory.WARNING,
    code: MessageCode.EXPORT_NO_DATA,
  },
  [MessageCode.EXPORT_SUCCESS]: {
    text: MSG_EXPORT_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.EXPORT_SUCCESS,
  },
  [MessageCode.EXPORT_FAILED]: {
    text: MSG_EXPORT_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.EXPORT_FAILED,
  },
  [MessageCode.EXPORT_SELECT_FIELD]: {
    text: MSG_EXPORT_SELECT_FIELD,
    category: MessageCategory.WARNING,
    code: MessageCode.EXPORT_SELECT_FIELD,
  },
  
  // Item Create/Edit Messages
  [MessageCode.VALIDATION_REQUIRED_FIELDS]: {
    text: MSG_ITEM_VALIDATION_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.VALIDATION_REQUIRED_FIELDS,
  },
  [MessageCode.NAVIGATION_ERROR]: {
    text: MSG_ITEM_NAVIGATION_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.NAVIGATION_ERROR,
  },
  
  // Brand Messages
  [MessageCode.BRAND_CREATE_SUCCESS]: {
    text: MSG_BRAND_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.BRAND_CREATE_SUCCESS,
  },
  [MessageCode.BRAND_UPDATE_SUCCESS]: {
    text: MSG_BRAND_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.BRAND_UPDATE_SUCCESS,
  },
  [MessageCode.BRAND_DELETE_SUCCESS]: {
    text: MSG_BRAND_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.BRAND_DELETE_SUCCESS,
  },
  [MessageCode.BRAND_DELETE_FAILED]: {
    text: MSG_BRAND_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.BRAND_DELETE_FAILED,
  },
  [MessageCode.BRAND_DELETE_IN_USE]: {
    text: MSG_BRAND_DELETE_IN_USE,
    category: MessageCategory.ERROR,
    code: MessageCode.BRAND_DELETE_IN_USE,
  },
  [MessageCode.BRAND_ALREADY_EXISTS]: {
    text: MSG_BRAND_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.BRAND_ALREADY_EXISTS,
  },
  [MessageCode.BRAND_REQUIRED_FIELDS]: {
    text: MSG_BRAND_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.BRAND_REQUIRED_FIELDS,
  },
  [MessageCode.BRAND_NAME_REQUIRED]: {
    text: MSG_BRAND_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.BRAND_NAME_REQUIRED,
  },
  [MessageCode.BRAND_UNEXPECTED_ERROR]: {
    text: MSG_BRAND_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.BRAND_UNEXPECTED_ERROR,
  },
  [MessageCode.BRAND_FETCH_FAILED]: {
    text: MSG_BRAND_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.BRAND_FETCH_FAILED,
  },
  [MessageCode.BRAND_SEARCH_PLACEHOLDER]: {
    text: MSG_BRAND_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.BRAND_SEARCH_PLACEHOLDER,
  },
  [MessageCode.BRAND_INLINE_EDIT_NOT_SUPPORTED]: {
    text: MSG_BRAND_INLINE_EDIT_NOT_SUPPORTED,
    category: MessageCategory.WARNING,
    code: MessageCode.BRAND_INLINE_EDIT_NOT_SUPPORTED,
  },
  [MessageCode.BRAND_CONFIRM_DELETE]: {
    text: MSG_BRAND_CONFIRM_DELETE,
    category: MessageCategory.INFO,
    code: MessageCode.BRAND_CONFIRM_DELETE,
  },
  [MessageCode.BRAND_CONFIRM_DELETE_NAMED]: {
    text: MSG_BRAND_CONFIRM_DELETE_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.BRAND_CONFIRM_DELETE_NAMED,
  },

  // Main Category Messages - COMPLETE
  [MessageCode.MAINCAT_VIEW_TITLE]: {
    text: UI_MAINCAT_VIEW_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_VIEW_TITLE,
  },
  [MessageCode.MAINCAT_BREADCRUMB_VIEW]: {
    text: UI_MAINCAT_BREADCRUMB_VIEW,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_BREADCRUMB_VIEW,
  },
  [MessageCode.MAINCAT_TABLE_TITLE]: {
    text: UI_MAINCAT_TABLE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_TABLE_TITLE,
  },
  [MessageCode.MAINCAT_CREATE_SUCCESS]: {
    text: MSG_MAINCAT_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MAINCAT_CREATE_SUCCESS,
  },
  [MessageCode.MAINCAT_UPDATE_SUCCESS]: {
    text: MSG_MAINCAT_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MAINCAT_UPDATE_SUCCESS,
  },
  [MessageCode.MAINCAT_DELETE_SUCCESS]: {
    text: MSG_MAINCAT_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MAINCAT_DELETE_SUCCESS,
  },
  [MessageCode.MAINCAT_ALREADY_EXISTS]: {
    text: MSG_MAINCAT_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.MAINCAT_ALREADY_EXISTS,
  },
  [MessageCode.MAINCAT_REQUIRED_FIELDS]: {
    text: MSG_MAINCAT_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.MAINCAT_REQUIRED_FIELDS,
  },
  [MessageCode.MAINCAT_NAME_REQUIRED]: {
    text: MSG_MAINCAT_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.MAINCAT_NAME_REQUIRED,
  },
  [MessageCode.MAINCAT_NO_CATEGORY_SELECTED]: {
    text: MSG_MAINCAT_NO_CATEGORY_SELECTED,
    category: MessageCategory.WARNING,
    code: MessageCode.MAINCAT_NO_CATEGORY_SELECTED,
  },
  [MessageCode.MAINCAT_FETCH_FAILED]: {
    text: MSG_MAINCAT_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_FETCH_FAILED,
  },
  [MessageCode.MAINCAT_DELETE_FAILED]: {
    text: MSG_MAINCAT_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_DELETE_FAILED,
  },
  [MessageCode.MAINCAT_UNEXPECTED_ERROR]: {
    text: MSG_MAINCAT_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_UNEXPECTED_ERROR,
  },
  [MessageCode.MAINCAT_COMPCODE_MISSING]: {
    text: MSG_MAINCAT_COMPCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_COMPCODE_MISSING,
  },
  [MessageCode.MAINCAT_LOCCODE_MISSING]: {
    text: MSG_MAINCAT_LOCCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_LOCCODE_MISSING,
  },
  [MessageCode.MAINCAT_USERNAME_MISSING]: {
    text: MSG_MAINCAT_USERNAME_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MAINCAT_USERNAME_MISSING,
  },
  [MessageCode.MAINCAT_SEARCH_PLACEHOLDER]: {
    text: UI_MAINCAT_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_SEARCH_PLACEHOLDER,
  },
  [MessageCode.MAINCAT_DELETE_TITLE]: {
    text: UI_MAINCAT_DELETE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_DELETE_TITLE,
  },
  [MessageCode.MAINCAT_DELETE_CONFIRMATION]: {
    text: UI_MAINCAT_DELETE_CONFIRMATION,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_DELETE_CONFIRMATION,
  },
  [MessageCode.MAINCAT_DELETE_CONFIRMATION_NAMED]: {
    text: UI_MAINCAT_DELETE_CONFIRMATION_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_DELETE_CONFIRMATION_NAMED,
  },
  [MessageCode.MAINCAT_DELETING]: {
    text: UI_MAINCAT_DELETING,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_DELETING,
  },
  [MessageCode.MAINCAT_ADD_BUTTON]: {
    text: UI_MAINCAT_ADD_BUTTON,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_ADD_BUTTON,
  },
  [MessageCode.MAINCAT_EDIT_ACTION]: {
    text: UI_MAINCAT_EDIT_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_EDIT_ACTION,
  },
  [MessageCode.MAINCAT_DELETE_ACTION]: {
    text: UI_MAINCAT_DELETE_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_DELETE_ACTION,
  },
  [MessageCode.MAINCAT_COL_CODE]: {
    text: UI_MAINCAT_COL_CODE,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_COL_CODE,
  },
  [MessageCode.MAINCAT_COL_NAME]: {
    text: UI_MAINCAT_COL_NAME,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_COL_NAME,
  },
  [MessageCode.MAINCAT_COL_DESCRIPTION]: {
    text: UI_MAINCAT_COL_DESCRIPTION,
    category: MessageCategory.INFO,
    code: MessageCode.MAINCAT_COL_DESCRIPTION,
  },
  
  // Category Messages - COMPLETE
  [MessageCode.CATEGORY_VIEW_TITLE]: {
    text: UI_CATEGORY_VIEW_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_VIEW_TITLE,
  },
  [MessageCode.CATEGORY_BREADCRUMB_VIEW]: {
    text: UI_CATEGORY_BREADCRUMB_VIEW,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_BREADCRUMB_VIEW,
  },
  [MessageCode.CATEGORY_TABLE_TITLE]: {
    text: UI_CATEGORY_TABLE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_TABLE_TITLE,
  },
  [MessageCode.CATEGORY_CREATE_SUCCESS]: {
    text: MSG_CATEGORY_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.CATEGORY_CREATE_SUCCESS,
  },
  [MessageCode.CATEGORY_UPDATE_SUCCESS]: {
    text: MSG_CATEGORY_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.CATEGORY_UPDATE_SUCCESS,
  },
  [MessageCode.CATEGORY_DELETE_SUCCESS]: {
    text: MSG_CATEGORY_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.CATEGORY_DELETE_SUCCESS,
  },
  [MessageCode.CATEGORY_DELETE_FAILED]: {
    text: MSG_CATEGORY_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_DELETE_FAILED,
  },
  [MessageCode.CATEGORY_ALREADY_EXISTS]: {
    text: MSG_CATEGORY_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.CATEGORY_ALREADY_EXISTS,
  },
  [MessageCode.CATEGORY_REQUIRED_FIELDS]: {
    text: MSG_CATEGORY_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.CATEGORY_REQUIRED_FIELDS,
  },
  [MessageCode.CATEGORY_NAME_REQUIRED]: {
    text: MSG_CATEGORY_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.CATEGORY_NAME_REQUIRED,
  },
  [MessageCode.CATEGORY_MAIN_CATEGORY_REQUIRED]: {
    text: MSG_CATEGORY_MAIN_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.CATEGORY_MAIN_CATEGORY_REQUIRED,
  },
  [MessageCode.CATEGORY_FETCH_FAILED]: {
    text: MSG_CATEGORY_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_FETCH_FAILED,
  },
  [MessageCode.CATEGORY_FETCH_CATEGORIES_FAILED]: {
    text: MSG_CATEGORY_FETCH_CATEGORIES_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_FETCH_CATEGORIES_FAILED,
  },
  [MessageCode.CATEGORY_CREATE_FAILED]: {
    text: MSG_CATEGORY_CREATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_CREATE_FAILED,
  },
  [MessageCode.CATEGORY_UPDATE_FAILED]: {
    text: MSG_CATEGORY_UPDATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_UPDATE_FAILED,
  },
  [MessageCode.CATEGORY_UNEXPECTED_ERROR]: {
    text: MSG_CATEGORY_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_UNEXPECTED_ERROR,
  },
  [MessageCode.CATEGORY_NO_CATEGORY_SELECTED]: {
    text: MSG_CATEGORY_NO_CATEGORY_SELECTED,
    category: MessageCategory.WARNING,
    code: MessageCode.CATEGORY_NO_CATEGORY_SELECTED,
  },
  [MessageCode.CATEGORY_COMPCODE_MISSING]: {
    text: MSG_CATEGORY_COMPCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_COMPCODE_MISSING,
  },
  [MessageCode.CATEGORY_LOCCODE_MISSING]: {
    text: MSG_CATEGORY_LOCCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_LOCCODE_MISSING,
  },
  [MessageCode.CATEGORY_ENUSER_MISSING]: {
    text: MSG_CATEGORY_ENUSER_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.CATEGORY_ENUSER_MISSING,
  },
  [MessageCode.CATEGORY_SEARCH_PLACEHOLDER]: {
    text: UI_CATEGORY_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_SEARCH_PLACEHOLDER,
  },
  [MessageCode.CATEGORY_DELETE_TITLE]: {
    text: UI_CATEGORY_DELETE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_DELETE_TITLE,
  },
  [MessageCode.CATEGORY_DELETE_CONFIRMATION]: {
    text: UI_CATEGORY_DELETE_CONFIRMATION,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_DELETE_CONFIRMATION,
  },
  [MessageCode.CATEGORY_DELETE_CONFIRMATION_NAMED]: {
    text: UI_CATEGORY_DELETE_CONFIRMATION_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_DELETE_CONFIRMATION_NAMED,
  },
  [MessageCode.CATEGORY_DELETING]: {
    text: UI_CATEGORY_DELETING,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_DELETING,
  },
  [MessageCode.CATEGORY_LABEL_MAIN_CATEGORY]: {
    text: UI_CATEGORY_LABEL_MAIN_CATEGORY,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_LABEL_MAIN_CATEGORY,
  },
  [MessageCode.CATEGORY_ADD_BUTTON]: {
    text: UI_CATEGORY_ADD_BUTTON,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_ADD_BUTTON,
  },
  [MessageCode.CATEGORY_EDIT_ACTION]: {
    text: UI_CATEGORY_EDIT_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_EDIT_ACTION,
  },
  [MessageCode.CATEGORY_DELETE_ACTION]: {
    text: UI_CATEGORY_DELETE_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.CATEGORY_DELETE_ACTION,
  },
  
  // Generic Name Messages - COMPLETE
  [MessageCode.GENERIC_VIEW_TITLE]: {
    text: UI_GENERIC_VIEW_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_VIEW_TITLE,
  },
  [MessageCode.GENERIC_BREADCRUMB_VIEW]: {
    text: UI_GENERIC_BREADCRUMB_VIEW,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_BREADCRUMB_VIEW,
  },
  [MessageCode.GENERIC_TABLE_TITLE]: {
    text: UI_GENERIC_TABLE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_TABLE_TITLE,
  },
  [MessageCode.GENERIC_CREATE_SUCCESS]: {
    text: MSG_GENERIC_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GENERIC_CREATE_SUCCESS,
  },
  [MessageCode.GENERIC_UPDATE_SUCCESS]: {
    text: MSG_GENERIC_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GENERIC_UPDATE_SUCCESS,
  },
  [MessageCode.GENERIC_DELETE_SUCCESS]: {
    text: MSG_GENERIC_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.GENERIC_DELETE_SUCCESS,
  },
  [MessageCode.GENERIC_ALREADY_EXISTS]: {
    text: MSG_GENERIC_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_ALREADY_EXISTS,
  },
  [MessageCode.GENERIC_REQUIRED_FIELDS]: {
    text: MSG_GENERIC_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_REQUIRED_FIELDS,
  },
  [MessageCode.GENERIC_NAME_REQUIRED]: {
    text: MSG_GENERIC_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_NAME_REQUIRED,
  },
  [MessageCode.GENERIC_MAIN_CATEGORY_REQUIRED]: {
    text: MSG_GENERIC_MAIN_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_MAIN_CATEGORY_REQUIRED,
  },
  [MessageCode.GENERIC_CATEGORY_REQUIRED]: {
    text: MSG_GENERIC_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_CATEGORY_REQUIRED,
  },
  [MessageCode.GENERIC_SUBCATEGORY_REQUIRED]: {
    text: MSG_GENERIC_SUBCATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_SUBCATEGORY_REQUIRED,
  },
  [MessageCode.GENERIC_LEADING_SPACES]: {
    text: MSG_GENERIC_LEADING_SPACES,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_LEADING_SPACES,
  },
  [MessageCode.GENERIC_FETCH_INITIAL_FAILED]: {
    text: MSG_GENERIC_FETCH_INITIAL_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_FETCH_INITIAL_FAILED,
  },
  [MessageCode.GENERIC_CREATE_FAILED]: {
    text: MSG_GENERIC_CREATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_CREATE_FAILED,
  },
  [MessageCode.GENERIC_UPDATE_FAILED]: {
    text: MSG_GENERIC_UPDATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_UPDATE_FAILED,
  },
  [MessageCode.GENERIC_DELETE_FAILED]: {
    text: MSG_GENERIC_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_DELETE_FAILED,
  },
  [MessageCode.GENERIC_DELETE_IN_USE]: {
    text: MSG_GENERIC_DELETE_IN_USE,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_DELETE_IN_USE,
  },
  [MessageCode.GENERIC_UNEXPECTED_ERROR]: {
    text: MSG_GENERIC_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_UNEXPECTED_ERROR,
  },
  [MessageCode.GENERIC_NO_ITEM_SELECTED]: {
    text: MSG_GENERIC_NO_ITEM_SELECTED,
    category: MessageCategory.WARNING,
    code: MessageCode.GENERIC_NO_ITEM_SELECTED,
  },
  [MessageCode.GENERIC_COMPCODE_MISSING]: {
    text: MSG_GENERIC_COMPCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_COMPCODE_MISSING,
  },
  [MessageCode.GENERIC_LOCCODE_MISSING]: {
    text: MSG_GENERIC_LOCCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_LOCCODE_MISSING,
  },
  [MessageCode.GENERIC_USERNAME_MISSING]: {
    text: MSG_GENERIC_USERNAME_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.GENERIC_USERNAME_MISSING,
  },
  [MessageCode.GENERIC_SEARCH_PLACEHOLDER]: {
    text: UI_GENERIC_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_SEARCH_PLACEHOLDER,
  },
  [MessageCode.GENERIC_DELETE_TITLE]: {
    text: UI_GENERIC_DELETE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_DELETE_TITLE,
  },
  [MessageCode.GENERIC_DELETE_CONFIRMATION]: {
    text: UI_GENERIC_DELETE_CONFIRMATION,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_DELETE_CONFIRMATION,
  },
  [MessageCode.GENERIC_DELETE_CONFIRMATION_NAMED]: {
    text: UI_GENERIC_DELETE_CONFIRMATION_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_DELETE_CONFIRMATION_NAMED,
  },
  [MessageCode.GENERIC_DELETING]: {
    text: UI_GENERIC_DELETING,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_DELETING,
  },
  [MessageCode.GENERIC_ADD_BUTTON]: {
    text: UI_GENERIC_ADD_BUTTON,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_ADD_BUTTON,
  },
  [MessageCode.GENERIC_EDIT_ACTION]: {
    text: UI_GENERIC_EDIT_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_EDIT_ACTION,
  },
  [MessageCode.GENERIC_DELETE_ACTION]: {
    text: UI_GENERIC_DELETE_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_DELETE_ACTION,
  },
  [MessageCode.GENERIC_COL_ID]: {
    text: UI_GENERIC_COL_ID,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_COL_ID,
  },
  [MessageCode.GENERIC_COL_SUBCATEGORY]: {
    text: UI_GENERIC_COL_SUBCATEGORY,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_COL_SUBCATEGORY,
  },
  [MessageCode.GENERIC_COL_NAME]: {
    text: UI_GENERIC_COL_NAME,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_COL_NAME,
  },
  [MessageCode.GENERIC_COL_DESCRIPTION]: {
    text: UI_GENERIC_COL_DESCRIPTION,
    category: MessageCategory.INFO,
    code: MessageCode.GENERIC_COL_DESCRIPTION,
  },
  
  // Subcategory Messages
  [MessageCode.SUBCATEGORY_VIEW_TITLE]: {
    text: UI_SUBCATEGORY_VIEW_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_VIEW_TITLE,
  },
  [MessageCode.SUBCATEGORY_BREADCRUMB_VIEW]: {
    text: UI_SUBCATEGORY_BREADCRUMB_VIEW,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_BREADCRUMB_VIEW,
  },
  [MessageCode.SUBCATEGORY_TABLE_TITLE]: {
    text: UI_SUBCATEGORY_TABLE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_TABLE_TITLE,
  },
  [MessageCode.SUBCATEGORY_CREATE_SUCCESS]: {
    text: MSG_SUBCATEGORY_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.SUBCATEGORY_CREATE_SUCCESS,
  },
  [MessageCode.SUBCATEGORY_UPDATE_SUCCESS]: {
    text: MSG_SUBCATEGORY_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.SUBCATEGORY_UPDATE_SUCCESS,
  },
  [MessageCode.SUBCATEGORY_DELETE_SUCCESS]: {
    text: MSG_SUBCATEGORY_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.SUBCATEGORY_DELETE_SUCCESS,
  },
  [MessageCode.SUBCATEGORY_DELETE_FAILED]: {
    text: MSG_SUBCATEGORY_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_DELETE_FAILED,
  },
  [MessageCode.SUBCATEGORY_DELETE_IN_USE]: {
    text: MSG_SUBCATEGORY_DELETE_IN_USE,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_DELETE_IN_USE,
  },
  [MessageCode.SUBCATEGORY_ALREADY_EXISTS]: {
    text: MSG_SUBCATEGORY_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_ALREADY_EXISTS,
  },
  [MessageCode.SUBCATEGORY_REQUIRED_FIELDS]: {
    text: MSG_SUBCATEGORY_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_REQUIRED_FIELDS,
  },
  [MessageCode.SUBCATEGORY_NAME_REQUIRED]: {
    text: MSG_SUBCATEGORY_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_NAME_REQUIRED,
  },
  [MessageCode.SUBCATEGORY_MAIN_CATEGORY_REQUIRED]: {
    text: MSG_SUBCATEGORY_MAIN_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_MAIN_CATEGORY_REQUIRED,
  },
  [MessageCode.SUBCATEGORY_CATEGORY_REQUIRED]: {
    text: MSG_SUBCATEGORY_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_CATEGORY_REQUIRED,
  },
  [MessageCode.SUBCATEGORY_FETCH_FAILED]: {
    text: MSG_SUBCATEGORY_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_FETCH_FAILED,
  },
  [MessageCode.SUBCATEGORY_FETCH_CATEGORIES_FAILED]: {
    text: MSG_SUBCATEGORY_FETCH_CATEGORIES_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_FETCH_CATEGORIES_FAILED,
  },
  [MessageCode.SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED]: {
    text: MSG_SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED,
  },
  [MessageCode.SUBCATEGORY_CREATE_FAILED]: {
    text: MSG_SUBCATEGORY_CREATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_CREATE_FAILED,
  },
  [MessageCode.SUBCATEGORY_UPDATE_FAILED]: {
    text: MSG_SUBCATEGORY_UPDATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_UPDATE_FAILED,
  },
  [MessageCode.SUBCATEGORY_UNEXPECTED_ERROR]: {
    text: MSG_SUBCATEGORY_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_UNEXPECTED_ERROR,
  },
  [MessageCode.SUBCATEGORY_NO_SUBCATEGORY_SELECTED]: {
    text: MSG_SUBCATEGORY_NO_SUBCATEGORY_SELECTED,
    category: MessageCategory.WARNING,
    code: MessageCode.SUBCATEGORY_NO_SUBCATEGORY_SELECTED,
  },
  [MessageCode.SUBCATEGORY_COMPCODE_MISSING]: {
    text: MSG_SUBCATEGORY_COMPCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_COMPCODE_MISSING,
  },
  [MessageCode.SUBCATEGORY_LOCCODE_MISSING]: {
    text: MSG_SUBCATEGORY_LOCCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_LOCCODE_MISSING,
  },
  [MessageCode.SUBCATEGORY_ENUSER_MISSING]: {
    text: MSG_SUBCATEGORY_ENUSER_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.SUBCATEGORY_ENUSER_MISSING,
  },
  [MessageCode.SUBCATEGORY_SEARCH_PLACEHOLDER]: {
    text: UI_SUBCATEGORY_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_SEARCH_PLACEHOLDER,
  },
  [MessageCode.SUBCATEGORY_DELETE_TITLE]: {
    text: UI_SUBCATEGORY_DELETE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_DELETE_TITLE,
  },
  [MessageCode.SUBCATEGORY_DELETE_CONFIRMATION]: {
    text: UI_SUBCATEGORY_DELETE_CONFIRMATION,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_DELETE_CONFIRMATION,
  },
  [MessageCode.SUBCATEGORY_DELETE_CONFIRMATION_NAMED]: {
    text: UI_SUBCATEGORY_DELETE_CONFIRMATION_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_DELETE_CONFIRMATION_NAMED,
  },
  [MessageCode.SUBCATEGORY_DELETING]: {
    text: UI_SUBCATEGORY_DELETING,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_DELETING,
  },
  [MessageCode.SUBCATEGORY_LABEL_MAIN_CATEGORY]: {
    text: UI_SUBCATEGORY_LABEL_MAIN_CATEGORY,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_LABEL_MAIN_CATEGORY,
  },
  [MessageCode.SUBCATEGORY_LABEL_CATEGORY]: {
    text: UI_SUBCATEGORY_LABEL_CATEGORY,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_LABEL_CATEGORY,
  },
  [MessageCode.SUBCATEGORY_ADD_BUTTON]: {
    text: UI_SUBCATEGORY_ADD_BUTTON,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_ADD_BUTTON,
  },
  [MessageCode.SUBCATEGORY_EDIT_ACTION]: {
    text: UI_SUBCATEGORY_EDIT_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_EDIT_ACTION,
  },
  [MessageCode.SUBCATEGORY_DELETE_ACTION]: {
    text: UI_SUBCATEGORY_DELETE_ACTION,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_DELETE_ACTION,
  },
  [MessageCode.SUBCATEGORY_COL_CODE]: {
    text: UI_SUBCATEGORY_COL_CODE,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_COL_CODE,
  },
  [MessageCode.SUBCATEGORY_COL_NAME]: {
    text: UI_SUBCATEGORY_COL_NAME,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_COL_NAME,
  },
  [MessageCode.SUBCATEGORY_COL_DESCRIPTION]: {
    text: UI_SUBCATEGORY_COL_DESCRIPTION,
    category: MessageCategory.INFO,
    code: MessageCode.SUBCATEGORY_COL_DESCRIPTION,
  },
  
  // Document Type Messages
  [MessageCode.DOCTYPE_CREATE_SUCCESS]: {
    text: MSG_DOCTYPE_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.DOCTYPE_CREATE_SUCCESS,
  },
  [MessageCode.DOCTYPE_UPDATE_SUCCESS]: {
    text: MSG_DOCTYPE_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.DOCTYPE_UPDATE_SUCCESS,
  },
  [MessageCode.DOCTYPE_ALREADY_EXISTS]: {
    text: MSG_DOCTYPE_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.DOCTYPE_ALREADY_EXISTS,
  },
  [MessageCode.DOCTYPE_REQUIRED_FIELDS]: {
    text: MSG_DOCTYPE_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.DOCTYPE_REQUIRED_FIELDS,
  },
  [MessageCode.DOCTYPE_NAME_REQUIRED]: {
    text: MSG_DOCTYPE_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.DOCTYPE_NAME_REQUIRED,
  },
  [MessageCode.DOCTYPE_CATEGORY_REQUIRED]: {
    text: MSG_DOCTYPE_CATEGORY_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.DOCTYPE_CATEGORY_REQUIRED,
  },
  [MessageCode.DOCTYPE_SAVE_CODE_REQUIRED]: {
    text: MSG_DOCTYPE_SAVE_CODE_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.DOCTYPE_SAVE_CODE_REQUIRED,
  },
  [MessageCode.DOCTYPE_FETCH_CATEGORIES_FAILED]: {
    text: MSG_DOCTYPE_FETCH_CATEGORIES_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_FETCH_CATEGORIES_FAILED,
  },
  [MessageCode.DOCTYPE_FETCH_TYPES_FAILED]: {
    text: MSG_DOCTYPE_FETCH_TYPES_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_FETCH_TYPES_FAILED,
  },
  [MessageCode.DOCTYPE_FETCH_ITEMS_FAILED]: {
    text: MSG_DOCTYPE_FETCH_ITEMS_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_FETCH_ITEMS_FAILED,
  },
  [MessageCode.DOCTYPE_CREATE_FAILED]: {
    text: MSG_DOCTYPE_CREATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_CREATE_FAILED,
  },
  [MessageCode.DOCTYPE_UPDATE_FAILED]: {
    text: MSG_DOCTYPE_UPDATE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_UPDATE_FAILED,
  },
  [MessageCode.DOCTYPE_UNEXPECTED_ERROR]: {
    text: MSG_DOCTYPE_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_UNEXPECTED_ERROR,
  },
  [MessageCode.DOCTYPE_INPUT_MAX_LENGTH]: {
    text: MSG_DOCTYPE_INPUT_MAX_LENGTH,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_INPUT_MAX_LENGTH,
  },
  [MessageCode.DOCTYPE_INPUT_EMPTY]: {
    text: MSG_DOCTYPE_INPUT_EMPTY,
    category: MessageCategory.ERROR,
    code: MessageCode.DOCTYPE_INPUT_EMPTY,
  },
  
  // Measuring Unit Messages
  [MessageCode.MEASURINGUNIT_CREATE_SUCCESS]: {
    text: MSG_MEASURINGUNIT_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MEASURINGUNIT_CREATE_SUCCESS,
  },
  [MessageCode.MEASURINGUNIT_UPDATE_SUCCESS]: {
    text: MSG_MEASURINGUNIT_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MEASURINGUNIT_UPDATE_SUCCESS,
  },
  [MessageCode.MEASURINGUNIT_DELETE_SUCCESS]: {
    text: MSG_MEASURINGUNIT_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.MEASURINGUNIT_DELETE_SUCCESS,
  },
  [MessageCode.MEASURINGUNIT_DELETE_FAILED]: {
    text: MSG_MEASURINGUNIT_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_DELETE_FAILED,
  },
  [MessageCode.MEASURINGUNIT_ALREADY_EXISTS]: {
    text: MSG_MEASURINGUNIT_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.MEASURINGUNIT_ALREADY_EXISTS,
  },
  [MessageCode.MEASURINGUNIT_REQUIRED_FIELDS]: {
    text: MSG_MEASURINGUNIT_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.MEASURINGUNIT_REQUIRED_FIELDS,
  },
  [MessageCode.MEASURINGUNIT_NAME_REQUIRED]: {
    text: MSG_MEASURINGUNIT_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.MEASURINGUNIT_NAME_REQUIRED,
  },
  [MessageCode.MEASURINGUNIT_UNEXPECTED_ERROR]: {
    text: MSG_MEASURINGUNIT_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_UNEXPECTED_ERROR,
  },
  [MessageCode.MEASURINGUNIT_FETCH_FAILED]: {
    text: MSG_MEASURINGUNIT_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_FETCH_FAILED,
  },
  [MessageCode.MEASURINGUNIT_INPUT_MAX_LENGTH]: {
    text: MSG_MEASURINGUNIT_INPUT_MAX_LENGTH,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_INPUT_MAX_LENGTH,
  },
  [MessageCode.MEASURINGUNIT_INPUT_EMPTY]: {
    text: MSG_MEASURINGUNIT_INPUT_EMPTY,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_INPUT_EMPTY,
  },
  [MessageCode.MEASURINGUNIT_COMPCODE_MISSING]: {
    text: MSG_MEASURINGUNIT_COMPCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_COMPCODE_MISSING,
  },
  [MessageCode.MEASURINGUNIT_LOCCODE_MISSING]: {
    text: MSG_MEASURINGUNIT_LOCCODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_LOCCODE_MISSING,
  },
  [MessageCode.MEASURINGUNIT_CODE_MISSING]: {
    text: MSG_MEASURINGUNIT_CODE_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_CODE_MISSING,
  },
  [MessageCode.MEASURINGUNIT_ENUSER_MISSING]: {
    text: MSG_MEASURINGUNIT_ENUSER_MISSING,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_ENUSER_MISSING,
  },
  [MessageCode.MEASURINGUNIT_DELETE_OPERATION_FAILED]: {
    text: MSG_MEASURINGUNIT_DELETE_OPERATION_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_DELETE_OPERATION_FAILED,
  },
  [MessageCode.MEASURINGUNIT_NULL_REFERENCE]: {
    text: MSG_MEASURINGUNIT_NULL_REFERENCE,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_NULL_REFERENCE,
  },
  [MessageCode.MEASURINGUNIT_NULL_REFERENCE_DETAIL]: {
    text: MSG_MEASURINGUNIT_NULL_REFERENCE_DETAIL,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_NULL_REFERENCE_DETAIL,
  },
  [MessageCode.MEASURINGUNIT_BAD_REQUEST]: {
    text: MSG_MEASURINGUNIT_BAD_REQUEST,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_BAD_REQUEST,
  },
  [MessageCode.MEASURINGUNIT_NOT_FOUND]: {
    text: MSG_MEASURINGUNIT_NOT_FOUND,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_NOT_FOUND,
  },
  [MessageCode.MEASURINGUNIT_METHOD_NOT_ALLOWED]: {
    text: MSG_MEASURINGUNIT_METHOD_NOT_ALLOWED,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_METHOD_NOT_ALLOWED,
  },
  [MessageCode.MEASURINGUNIT_SERVER_ERROR]: {
    text: MSG_MEASURINGUNIT_SERVER_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_SERVER_ERROR,
  },
  [MessageCode.MEASURINGUNIT_SERVER_ERROR_PREFIX]: {
    text: MSG_MEASURINGUNIT_SERVER_ERROR_PREFIX,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_SERVER_ERROR_PREFIX,
  },
  [MessageCode.MEASURINGUNIT_NO_RESPONSE]: {
    text: MSG_MEASURINGUNIT_NO_RESPONSE,
    category: MessageCategory.ERROR,
    code: MessageCode.MEASURINGUNIT_NO_RESPONSE,
  },
  [MessageCode.MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED]: {
    text: MSG_MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED,
    category: MessageCategory.WARNING,
    code: MessageCode.MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED,
  },
  [MessageCode.MEASURINGUNIT_SEARCH_PLACEHOLDER]: {
    text: UI_MEASURING_UNIT_SEARCH_PLACEHOLDER,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_SEARCH_PLACEHOLDER,
  },
  [MessageCode.MEASURINGUNIT_DELETE_TITLE]: {
    text: UI_MEASURING_UNIT_DELETE_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_DELETE_TITLE,
  },
  [MessageCode.MEASURINGUNIT_DELETE_CONFIRMATION]: {
    text: UI_MEASURING_UNIT_DELETE_CONFIRMATION,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_DELETE_CONFIRMATION,
  },
  [MessageCode.MEASURINGUNIT_DELETE_CONFIRMATION_NAMED]: {
    text: UI_MEASURING_UNIT_DELETE_CONFIRMATION_NAMED,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_DELETE_CONFIRMATION_NAMED,
  },
  [MessageCode.MEASURINGUNIT_DELETING]: {
    text: UI_MEASURING_UNIT_DELETING,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_DELETING,
  },
  [MessageCode.MEASURINGUNIT_VIEW_TITLE]: {
    text: UI_MEASURING_UNIT_VIEW_TITLE,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_VIEW_TITLE,
  },
  [MessageCode.MEASURINGUNIT_BREADCRUMB_VIEW]: {
    text: UI_MEASURING_UNIT_BREADCRUMB_VIEW,
    category: MessageCategory.INFO,
    code: MessageCode.MEASURINGUNIT_BREADCRUMB_VIEW,
  },
  
  // Package Messages
  [MessageCode.PACKAGE_CREATE_SUCCESS]: {
    text: MSG_PACKAGE_CREATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.PACKAGE_CREATE_SUCCESS,
  },
  [MessageCode.PACKAGE_UPDATE_SUCCESS]: {
    text: MSG_PACKAGE_UPDATE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.PACKAGE_UPDATE_SUCCESS,
  },
  [MessageCode.PACKAGE_DELETE_SUCCESS]: {
    text: MSG_PACKAGE_DELETE_SUCCESS,
    category: MessageCategory.SUCCESS,
    code: MessageCode.PACKAGE_DELETE_SUCCESS,
  },
  [MessageCode.PACKAGE_DELETE_FAILED]: {
    text: MSG_PACKAGE_DELETE_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.PACKAGE_DELETE_FAILED,
  },
  [MessageCode.PACKAGE_ALREADY_EXISTS]: {
    text: MSG_PACKAGE_ALREADY_EXISTS,
    category: MessageCategory.WARNING,
    code: MessageCode.PACKAGE_ALREADY_EXISTS,
  },
  [MessageCode.PACKAGE_REQUIRED_FIELDS]: {
    text: MSG_PACKAGE_REQUIRED_FIELDS,
    category: MessageCategory.WARNING,
    code: MessageCode.PACKAGE_REQUIRED_FIELDS,
  },
  [MessageCode.PACKAGE_NAME_REQUIRED]: {
    text: MSG_PACKAGE_NAME_REQUIRED,
    category: MessageCategory.WARNING,
    code: MessageCode.PACKAGE_NAME_REQUIRED,
  },
  [MessageCode.PACKAGE_UNEXPECTED_ERROR]: {
    text: MSG_PACKAGE_UNEXPECTED_ERROR,
    category: MessageCategory.ERROR,
    code: MessageCode.PACKAGE_UNEXPECTED_ERROR,
  },
  [MessageCode.PACKAGE_FETCH_FAILED]: {
    text: MSG_PACKAGE_FETCH_FAILED,
    category: MessageCategory.ERROR,
    code: MessageCode.PACKAGE_FETCH_FAILED,
  },
  [MessageCode.NO_PACKAGE_SELECTED]: {
    text: MSG_NO_PACKAGE_SELECTED,
    category: MessageCategory.WARNING,
    code: MessageCode.NO_PACKAGE_SELECTED,
  },
  
  // UI Text Messages
  [MessageCode.UI_BREADCRUMB_HOME]: {
    text: UI_BREADCRUMB_HOME,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BREADCRUMB_HOME,
  },
  [MessageCode.UI_BREADCRUMB_SETTINGS]: {
    text: UI_BREADCRUMB_SETTINGS,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BREADCRUMB_SETTINGS,
  },
  [MessageCode.UI_BREADCRUMB_MEASURING_UNIT]: {
    text: UI_BREADCRUMB_MEASURING_UNIT,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BREADCRUMB_MEASURING_UNIT,
  },
  [MessageCode.UI_BREADCRUMB_PACKAGE]: {
    text: UI_BREADCRUMB_PACKAGE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BREADCRUMB_PACKAGE,
  },
  [MessageCode.UI_PAGE_TITLE_CREATE]: {
    text: UI_PAGE_TITLE_CREATE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_PAGE_TITLE_CREATE,
  },
  [MessageCode.UI_PAGE_TITLE_EDIT]: {
    text: UI_PAGE_TITLE_EDIT,
    category: MessageCategory.INFO,
    code: MessageCode.UI_PAGE_TITLE_EDIT,
  },
  [MessageCode.UI_PAGE_TITLE_CREATE_PACKAGE]: {
    text: UI_PAGE_TITLE_CREATE_PACKAGE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_PAGE_TITLE_CREATE_PACKAGE,
  },
  [MessageCode.UI_PAGE_TITLE_EDIT_PACKAGE]: {
    text: UI_PAGE_TITLE_EDIT_PACKAGE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_PAGE_TITLE_EDIT_PACKAGE,
  },
  [MessageCode.UI_BUTTON_CLEAR]: {
    text: UI_BUTTON_CLEAR,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_CLEAR,
  },
  [MessageCode.UI_BUTTON_CONFIRM]: {
    text: UI_BUTTON_CONFIRM,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_CONFIRM,
  },
  [MessageCode.UI_BUTTON_MODIFY]: {
    text: UI_BUTTON_MODIFY,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_MODIFY,
  },
  [MessageCode.UI_BUTTON_CANCEL]: {
    text: UI_BUTTON_CANCEL,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_CANCEL,
  },
  [MessageCode.UI_BUTTON_DELETE]: {
    text: UI_BUTTON_DELETE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_DELETE,
  },
  [MessageCode.UI_BUTTON_ADD]: {
    text: UI_BUTTON_ADD,
    category: MessageCategory.INFO,
    code: MessageCode.UI_BUTTON_ADD,
  },
  [MessageCode.UI_TOOLTIP_BACK]: {
    text: UI_TOOLTIP_BACK,
    category: MessageCategory.INFO,
    code: MessageCode.UI_TOOLTIP_BACK,
  },
  [MessageCode.UI_LABEL_MEASURING_UNIT]: {
    text: UI_LABEL_MEASURING_UNIT,
    category: MessageCategory.INFO,
    code: MessageCode.UI_LABEL_MEASURING_UNIT,
  },
  [MessageCode.UI_LABEL_DESCRIPTION]: {
    text: UI_LABEL_DESCRIPTION,
    category: MessageCategory.INFO,
    code: MessageCode.UI_LABEL_DESCRIPTION,
  },
  [MessageCode.UI_LABEL_PACKAGE_NAME]: {
    text: UI_LABEL_PACKAGE_NAME,
    category: MessageCategory.INFO,
    code: MessageCode.UI_LABEL_PACKAGE_NAME,
  },
  [MessageCode.UI_ACTION_EDIT]: {
    text: UI_ACTION_EDIT,
    category: MessageCategory.INFO,
    code: MessageCode.UI_ACTION_EDIT,
  },
  [MessageCode.UI_ACTION_DELETE]: {
    text: UI_ACTION_DELETE,
    category: MessageCategory.INFO,
    code: MessageCode.UI_ACTION_DELETE,
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets a message by its code
 * @param code - The message code
 * @returns The structured message object
 */
export const getMessageByCode = (code: MessageCode): AppMessage => {
  return STRUCTURED_MESSAGES[code];
};

/**
 * Gets a dynamic file size exceeded message
 * @param maxSizeMB - Maximum file size in MB
 * @returns Structured message with dynamic text
 */
export const getFileSizeExceededMessage = (maxSizeMB: number): AppMessage => {
  return {
    ...STRUCTURED_MESSAGES[MessageCode.FILE_SIZE_EXCEEDED],
    text: getMsgFileSizeExceeded(maxSizeMB),
  };
};

/**
 * Gets a dynamic items imported success message
 * @param count - Number of items imported
 * @returns Structured message with dynamic text
 */
export const getItemsImportedMessage = (count: number): AppMessage => {
  return {
    ...STRUCTURED_MESSAGES[MessageCode.ITEMS_IMPORTED],
    text: getSuccessItemsImported(count),
  };
};

/**
 * Gets a dynamic item delete success message
 * @param itemName - Name of the deleted item
 * @returns Structured message with dynamic text
 */
export const getItemDeleteSuccessMessage = (itemName: string): AppMessage => {
  return {
    ...STRUCTURED_MESSAGES[MessageCode.ITEM_DELETE_SUCCESS],
    text: MSG_ITEM_DELETE_SUCCESS(itemName),
  };
};

/**
 * Creates a dynamic message with parameters
 * @param template - Message template function
 * @param params - Parameters for the template
 * @returns Formatted message string
 */
export const createDynamicMessage = <T extends any[]>(
  template: (...args: T) => string,
  ...params: T
): string => {
  return template(...params);
};

// ============================================================================
// Message Groups for Feature-Based Organization
// ============================================================================

export const GrnMessages = {
  success: {
    create: MSG_GRN_SUCCESS,
    save: MSG_GRN_SAVE,
    hold: MSG_GRN_HOLD,
    update: MSG_GRN_UPDATE,
    delete: MSG_GRN_DELETE,
  },
  error: {
    create: MSG_GRN_FAILED,
    save: MSG_GRN_SAVE_FAILED,
    hold: MSG_GRN_HOLD_FAILED,
    update: MSG_GRN_UPDATE_FAILED,
    delete: MSG_GRN_DELETE_FAILED,
  },
};

export const AuthMessages = {
  success: LOGIN_SUCCESS,
  error: LOGIN_FAILED,
};

export const MigrationMessages = {
  invalidFileType: MSG_INVALID_FILE_TYPE,
  templateDownloaded: MSG_TEMPLATE_DOWNLOADED,
  fileSizeExceeded: getMsgFileSizeExceeded,
  itemsImported: getSuccessItemsImported,
  validation: {
    mandatoryMapping: VALIDATION_MANDATORY_MAPPING,
    skuDuplicate: VALIDATION_SKU_DUPLICATE,
    templateMismatch: VALIDATION_TEMPLATE_MISMATCH,
  },
  error: {
    parsingExcel: ERROR_PARSING_EXCEL,
    mergedCells: ERROR_MERGED_CELLS,
    invalidStartPoint: ERROR_INVALID_START_POINT,
    noData: ERROR_NO_DATA,
    failedToFetchData: ERROR_FAILED_TO_FETCH_DATA,
  },
};

export const ItemMessages = {
  loadFailed: MSG_ITEMS_LOAD_FAILED,
  deleteSuccess: MSG_ITEM_DELETE_SUCCESS,
  deleteFailed: MSG_ITEM_DELETE_FAILED,
  deleteOperationFailed: MSG_ITEM_DELETE_OPERATION_FAILED,
  deleteNoResponse: MSG_ITEM_DELETE_NO_RESPONSE,
  deleteUnknownError: MSG_ITEM_DELETE_UNKNOWN_ERROR,
  validation: {
    requiredFields: MSG_ITEM_VALIDATION_REQUIRED_FIELDS,
  },
  navigation: {
    error: MSG_ITEM_NAVIGATION_ERROR,
  },
};

export const ExportMessages = {
  noData: MSG_EXPORT_NO_DATA,
  success: MSG_EXPORT_SUCCESS,
  failed: MSG_EXPORT_FAILED,
  selectField: MSG_EXPORT_SELECT_FIELD,
};

export const BrandMessages = {
  success: {
    create: MSG_BRAND_CREATE_SUCCESS,
    update: MSG_BRAND_UPDATE_SUCCESS,
    delete: MSG_BRAND_DELETE_SUCCESS,
  },
  warning: {
    alreadyExists: MSG_BRAND_ALREADY_EXISTS,
    requiredFields: MSG_BRAND_REQUIRED_FIELDS,
    nameRequired: MSG_BRAND_NAME_REQUIRED,
    inlineEditNotSupported: MSG_BRAND_INLINE_EDIT_NOT_SUPPORTED,
  },
  error: {
    fetchFailed: MSG_BRAND_FETCH_FAILED,
    deleteFailed: MSG_BRAND_DELETE_FAILED,
    deleteInUse: MSG_BRAND_DELETE_IN_USE,
    unexpected: MSG_BRAND_UNEXPECTED_ERROR,
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
  ui: {
    searchPlaceholder: MSG_BRAND_SEARCH_PLACEHOLDER,
    confirmDelete: MSG_BRAND_CONFIRM_DELETE,
    confirmDeleteNamed: MSG_BRAND_CONFIRM_DELETE_NAMED,
  },
};

// ============================================================================
// Main Category Messages Group
// ============================================================================

export const MainCategoryMessages = {
  breadcrumb: {
    view: UI_MAINCAT_BREADCRUMB_VIEW,
  },
  pageTitle: {
    view: UI_MAINCAT_VIEW_TITLE,
  },
  tableTitle: UI_MAINCAT_TABLE_TITLE,
  success: {
    create: MSG_MAINCAT_CREATE_SUCCESS,
    update: MSG_MAINCAT_UPDATE_SUCCESS,
    delete: (itemName: string): string => MSG_MAINCAT_DELETE_SUCCESS.replace("{0}", itemName),
  },
  warning: {
    alreadyExists: MSG_MAINCAT_ALREADY_EXISTS,
    requiredFields: MSG_MAINCAT_REQUIRED_FIELDS,
    nameRequired: MSG_MAINCAT_NAME_REQUIRED,
    noCategorySelected: MSG_MAINCAT_NO_CATEGORY_SELECTED,
  },
  error: {
    fetchFailed: MSG_MAINCAT_FETCH_FAILED,
    deleteFailed: MSG_MAINCAT_DELETE_FAILED,
    unexpected: MSG_MAINCAT_UNEXPECTED_ERROR,
    compCodeMissing: MSG_MAINCAT_COMPCODE_MISSING,
    locCodeMissing: MSG_MAINCAT_LOCCODE_MISSING,
    usernameMissing: MSG_MAINCAT_USERNAME_MISSING,
  },
  placeholder: {
    search: UI_MAINCAT_SEARCH_PLACEHOLDER,
  },
  dialog: {
    deleteTitle: UI_MAINCAT_DELETE_TITLE,
    deleteConfirmation: UI_MAINCAT_DELETE_CONFIRMATION,
    deleteConfirmationNamed: (itemName: string): string => UI_MAINCAT_DELETE_CONFIRMATION_NAMED.replace("{0}", itemName),
  },
  button: {
    deleting: UI_MAINCAT_DELETING,
    add: UI_MAINCAT_ADD_BUTTON,
  },
  action: {
    edit: UI_MAINCAT_EDIT_ACTION,
    delete: UI_MAINCAT_DELETE_ACTION,
  },
  column: {
    code: UI_MAINCAT_COL_CODE,
    name: UI_MAINCAT_COL_NAME,
    description: UI_MAINCAT_COL_DESCRIPTION,
  },
};

// ============================================================================
// Category Messages Group
// ============================================================================

export const CategoryMessages = {
  breadcrumb: {
    view: UI_CATEGORY_BREADCRUMB_VIEW,
  },
  pageTitle: {
    view: UI_CATEGORY_VIEW_TITLE,
  },
  tableTitle: UI_CATEGORY_TABLE_TITLE,
  label: {
    mainCategory: UI_CATEGORY_LABEL_MAIN_CATEGORY,
  },
  success: {
    create: MSG_CATEGORY_CREATE_SUCCESS,
    update: MSG_CATEGORY_UPDATE_SUCCESS,
    delete: (itemName: string): string => MSG_CATEGORY_DELETE_SUCCESS.replace("{0}", itemName),
  },
  warning: {
    alreadyExists: MSG_CATEGORY_ALREADY_EXISTS,
    requiredFields: MSG_CATEGORY_REQUIRED_FIELDS,
    nameRequired: MSG_CATEGORY_NAME_REQUIRED,
    mainCategoryRequired: MSG_CATEGORY_MAIN_CATEGORY_REQUIRED,
    noCategorySelected: MSG_CATEGORY_NO_CATEGORY_SELECTED,
  },
  error: {
    fetchFailed: MSG_CATEGORY_FETCH_FAILED,
    fetchCategoriesFailed: MSG_CATEGORY_FETCH_CATEGORIES_FAILED,
    createFailed: MSG_CATEGORY_CREATE_FAILED,
    updateFailed: MSG_CATEGORY_UPDATE_FAILED,
    deleteFailed: MSG_CATEGORY_DELETE_FAILED,
    unexpected: MSG_CATEGORY_UNEXPECTED_ERROR,
    compCodeMissing: MSG_CATEGORY_COMPCODE_MISSING,
    locCodeMissing: MSG_CATEGORY_LOCCODE_MISSING,
    enuserMissing: MSG_CATEGORY_ENUSER_MISSING,
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
  placeholder: {
    search: UI_CATEGORY_SEARCH_PLACEHOLDER,
  },
  dialog: {
    deleteTitle: UI_CATEGORY_DELETE_TITLE,
    deleteConfirmation: UI_CATEGORY_DELETE_CONFIRMATION,
    deleteConfirmationNamed: (itemName: string): string => UI_CATEGORY_DELETE_CONFIRMATION_NAMED.replace("{0}", itemName),
  },
  button: {
    deleting: UI_CATEGORY_DELETING,
    add: UI_CATEGORY_ADD_BUTTON,
  },
  action: {
    edit: UI_CATEGORY_EDIT_ACTION,
    delete: UI_CATEGORY_DELETE_ACTION,
  },
};

// ============================================================================
// Generic Name Messages Group
// ============================================================================

export const GenericNameMessages = {
  breadcrumb: {
    view: UI_GENERIC_BREADCRUMB_VIEW,
  },
  pageTitle: {
    view: UI_GENERIC_VIEW_TITLE,
  },
  tableTitle: UI_GENERIC_TABLE_TITLE,
  success: {
    create: MSG_GENERIC_CREATE_SUCCESS,
    update: MSG_GENERIC_UPDATE_SUCCESS,
    delete: MSG_GENERIC_DELETE_SUCCESS,
  },
  warning: {
    alreadyExists: MSG_GENERIC_ALREADY_EXISTS,
    requiredFields: MSG_GENERIC_REQUIRED_FIELDS,
    nameRequired: MSG_GENERIC_NAME_REQUIRED,
    mainCategoryRequired: MSG_GENERIC_MAIN_CATEGORY_REQUIRED,
    categoryRequired: MSG_GENERIC_CATEGORY_REQUIRED,
    subcategoryRequired: MSG_GENERIC_SUBCATEGORY_REQUIRED,
    leadingSpaces: MSG_GENERIC_LEADING_SPACES,
    noItemSelected: MSG_GENERIC_NO_ITEM_SELECTED,
  },
  error: {
    fetchInitialFailed: MSG_GENERIC_FETCH_INITIAL_FAILED,
    createFailed: MSG_GENERIC_CREATE_FAILED,
    updateFailed: MSG_GENERIC_UPDATE_FAILED,
    deleteFailed: MSG_GENERIC_DELETE_FAILED,
    deleteInUse: MSG_GENERIC_DELETE_IN_USE,
    unexpected: MSG_GENERIC_UNEXPECTED_ERROR,
    compCodeMissing: MSG_GENERIC_COMPCODE_MISSING,
    locCodeMissing: MSG_GENERIC_LOCCODE_MISSING,
    usernameMissing: MSG_GENERIC_USERNAME_MISSING,
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
  placeholder: {
    search: UI_GENERIC_SEARCH_PLACEHOLDER,
  },
  dialog: {
    deleteTitle: UI_GENERIC_DELETE_TITLE,
    deleteConfirmation: UI_GENERIC_DELETE_CONFIRMATION,
    deleteConfirmationNamed: (itemName: string): string => UI_GENERIC_DELETE_CONFIRMATION_NAMED.replace("{0}", itemName),
  },
  button: {
    deleting: UI_GENERIC_DELETING,
    add: UI_GENERIC_ADD_BUTTON,
  },
  action: {
    edit: UI_GENERIC_EDIT_ACTION,
    delete: UI_GENERIC_DELETE_ACTION,
  },
  column: {
    id: UI_GENERIC_COL_ID,
    subcategory: UI_GENERIC_COL_SUBCATEGORY,
    name: UI_GENERIC_COL_NAME,
    description: UI_GENERIC_COL_DESCRIPTION,
  },
};

// ============================================================================
// Subcategory Messages Group
// ============================================================================

export const SubCategoryMessages = {
  breadcrumb: {
    view: UI_SUBCATEGORY_BREADCRUMB_VIEW,
  },
  pageTitle: {
    view: UI_SUBCATEGORY_VIEW_TITLE,
  },
  tableTitle: UI_SUBCATEGORY_TABLE_TITLE,
  label: {
    mainCategory: UI_SUBCATEGORY_LABEL_MAIN_CATEGORY,
    category: UI_SUBCATEGORY_LABEL_CATEGORY,
  },
  success: {
    create: MSG_SUBCATEGORY_CREATE_SUCCESS,
    update: MSG_SUBCATEGORY_UPDATE_SUCCESS,
    delete: MSG_SUBCATEGORY_DELETE_SUCCESS,
  },
  warning: {
    alreadyExists: MSG_SUBCATEGORY_ALREADY_EXISTS,
    requiredFields: MSG_SUBCATEGORY_REQUIRED_FIELDS,
    nameRequired: MSG_SUBCATEGORY_NAME_REQUIRED,
    mainCategoryRequired: MSG_SUBCATEGORY_MAIN_CATEGORY_REQUIRED,
    categoryRequired: MSG_SUBCATEGORY_CATEGORY_REQUIRED,
    noSubcategorySelected: MSG_SUBCATEGORY_NO_SUBCATEGORY_SELECTED,
  },
  error: {
    fetchFailed: MSG_SUBCATEGORY_FETCH_FAILED,
    fetchCategoriesFailed: MSG_SUBCATEGORY_FETCH_CATEGORIES_FAILED,
    fetchSubcategoriesFailed: MSG_SUBCATEGORY_FETCH_SUBCATEGORIES_FAILED,
    createFailed: MSG_SUBCATEGORY_CREATE_FAILED,
    updateFailed: MSG_SUBCATEGORY_UPDATE_FAILED,
    deleteFailed: MSG_SUBCATEGORY_DELETE_FAILED,
    deleteInUse: MSG_SUBCATEGORY_DELETE_IN_USE,
    unexpected: MSG_SUBCATEGORY_UNEXPECTED_ERROR,
    compCodeMissing: MSG_SUBCATEGORY_COMPCODE_MISSING,
    locCodeMissing: MSG_SUBCATEGORY_LOCCODE_MISSING,
    enuserMissing: MSG_SUBCATEGORY_ENUSER_MISSING,
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
  placeholder: {
    search: UI_SUBCATEGORY_SEARCH_PLACEHOLDER,
  },
  dialog: {
    deleteTitle: UI_SUBCATEGORY_DELETE_TITLE,
    deleteConfirmation: UI_SUBCATEGORY_DELETE_CONFIRMATION,
    deleteConfirmationNamed: (itemName: string): string => UI_SUBCATEGORY_DELETE_CONFIRMATION_NAMED.replace("{0}", itemName),
  },
  button: {
    deleting: UI_SUBCATEGORY_DELETING,
    add: UI_SUBCATEGORY_ADD_BUTTON,
  },
  action: {
    edit: UI_SUBCATEGORY_EDIT_ACTION,
    delete: UI_SUBCATEGORY_DELETE_ACTION,
  },
  column: {
    code: UI_SUBCATEGORY_COL_CODE,
    name: UI_SUBCATEGORY_COL_NAME,
    description: UI_SUBCATEGORY_COL_DESCRIPTION,
  },
};

// ============================================================================
// Document Type Messages Group
// ============================================================================

export const DocumentTypeMessages = {
  success: {
    create: MSG_DOCTYPE_CREATE_SUCCESS,
    update: MSG_DOCTYPE_UPDATE_SUCCESS,
  },
  warning: {
    alreadyExists: MSG_DOCTYPE_ALREADY_EXISTS,
    requiredFields: MSG_DOCTYPE_REQUIRED_FIELDS,
    nameRequired: MSG_DOCTYPE_NAME_REQUIRED,
    categoryRequired: MSG_DOCTYPE_CATEGORY_REQUIRED,
    saveCodeRequired: MSG_DOCTYPE_SAVE_CODE_REQUIRED,
  },
  error: {
    fetchCategoriesFailed: MSG_DOCTYPE_FETCH_CATEGORIES_FAILED,
    fetchTypesFailed: MSG_DOCTYPE_FETCH_TYPES_FAILED,
    fetchItemsFailed: MSG_DOCTYPE_FETCH_ITEMS_FAILED,
    createFailed: MSG_DOCTYPE_CREATE_FAILED,
    updateFailed: MSG_DOCTYPE_UPDATE_FAILED,
    unexpected: MSG_DOCTYPE_UNEXPECTED_ERROR,
    inputMaxLength: MSG_DOCTYPE_INPUT_MAX_LENGTH,
    inputEmpty: MSG_DOCTYPE_INPUT_EMPTY,
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
};

// ============================================================================
// Measuring Unit Messages Group
// ============================================================================

export const MeasuringUnitMessages = {
  breadcrumb: {
    view: UI_MEASURING_UNIT_BREADCRUMB_VIEW,
  },
  pageTitle: {
    view: UI_MEASURING_UNIT_VIEW_TITLE,
  },
  success: {
    create: MSG_MEASURINGUNIT_CREATE_SUCCESS,
    update: MSG_MEASURINGUNIT_UPDATE_SUCCESS,
    delete: (itemName: string): string => MSG_MEASURINGUNIT_DELETE_SUCCESS.replace("{0}", itemName),
  },
  warning: {
    alreadyExists: MSG_MEASURINGUNIT_ALREADY_EXISTS,
    requiredFields: MSG_MEASURINGUNIT_REQUIRED_FIELDS,
    nameRequired: MSG_MEASURINGUNIT_NAME_REQUIRED,
    inlineEditNotSupported: MSG_MEASURINGUNIT_INLINE_EDIT_NOT_SUPPORTED,
  },
  error: {
    fetchFailed: MSG_MEASURINGUNIT_FETCH_FAILED,
    deleteFailed: MSG_MEASURINGUNIT_DELETE_FAILED,
    deleteOperationFailed: MSG_MEASURINGUNIT_DELETE_OPERATION_FAILED,
    unexpected: MSG_MEASURINGUNIT_UNEXPECTED_ERROR,
    compCodeMissing: MSG_MEASURINGUNIT_COMPCODE_MISSING,
    locCodeMissing: MSG_MEASURINGUNIT_LOCCODE_MISSING,
    measuringUnitCodeMissing: MSG_MEASURINGUNIT_CODE_MISSING,
    enuserMissing: MSG_MEASURINGUNIT_ENUSER_MISSING,
    nullReference: MSG_MEASURINGUNIT_NULL_REFERENCE,
    nullReferenceDetail: MSG_MEASURINGUNIT_NULL_REFERENCE_DETAIL,
    badRequest: MSG_MEASURINGUNIT_BAD_REQUEST,
    notFound: MSG_MEASURINGUNIT_NOT_FOUND,
    methodNotAllowed: MSG_MEASURINGUNIT_METHOD_NOT_ALLOWED,
    serverError: MSG_MEASURINGUNIT_SERVER_ERROR,
    serverErrorPrefix: MSG_MEASURINGUNIT_SERVER_ERROR_PREFIX,
    noResponse: MSG_MEASURINGUNIT_NO_RESPONSE,
    inputMaxLength: MSG_MEASURINGUNIT_INPUT_MAX_LENGTH,
    inputEmpty: MSG_MEASURINGUNIT_INPUT_EMPTY,
    deleteInUse: "Measuring unit cannot delete it's already in use.",
  },
  validation: {
    inputMaxLength: getInputMaxLengthMessage,
  },
  placeholder: {
    search: UI_MEASURING_UNIT_SEARCH_PLACEHOLDER,
  },
  dialog: {
    deleteTitle: UI_MEASURING_UNIT_DELETE_TITLE,
    deleteConfirmation: UI_MEASURING_UNIT_DELETE_CONFIRMATION,
    deleteConfirmationNamed: (itemName: string): string => UI_MEASURING_UNIT_DELETE_CONFIRMATION_NAMED.replace("{0}", itemName),
  },
  button: {
    deleting: UI_MEASURING_UNIT_DELETING,
  },
};

// ============================================================================
// Package Messages Group
// ============================================================================

export const PackageMessages = {
  success: {
    create: MSG_PACKAGE_CREATE_SUCCESS,
    update: MSG_PACKAGE_UPDATE_SUCCESS,
    delete: MSG_PACKAGE_DELETE_SUCCESS,
  },
  warning: {
    alreadyExists: MSG_PACKAGE_ALREADY_EXISTS,
    requiredFields: MSG_PACKAGE_REQUIRED_FIELDS,
    nameRequired: MSG_PACKAGE_NAME_REQUIRED,
    noPackageSelected: MSG_NO_PACKAGE_SELECTED,
  },
  error: {
    fetchFailed: MSG_PACKAGE_FETCH_FAILED,
    unexpected: MSG_PACKAGE_UNEXPECTED_ERROR,
    deleteFailed: MSG_PACKAGE_DELETE_FAILED,
    deleteInUse: "Package cannot delete it's already in use.",
  },
  validation: {
    inputMaxLength: getFieldMaxLengthMessage,
  },
};

// ============================================================================
// UI Text Messages Group (Shared)
// ============================================================================

export const UITextMessages = {
  breadcrumb: {
    home: UI_BREADCRUMB_HOME,
    settings: UI_BREADCRUMB_SETTINGS,
    measuringUnit: UI_BREADCRUMB_MEASURING_UNIT,
    package: UI_BREADCRUMB_PACKAGE,
  },
  pageTitle: {
    create: UI_PAGE_TITLE_CREATE,
    edit: UI_PAGE_TITLE_EDIT,
    createPackage: UI_PAGE_TITLE_CREATE_PACKAGE,
    editPackage: UI_PAGE_TITLE_EDIT_PACKAGE,
  },
  button: {
    clear: UI_BUTTON_CLEAR,
    confirm: UI_BUTTON_CONFIRM,
    modify: UI_BUTTON_MODIFY,
    cancel: UI_BUTTON_CANCEL,
    delete: UI_BUTTON_DELETE,
    add: UI_BUTTON_ADD,
  },
  tooltip: {
    back: UI_TOOLTIP_BACK,
  },
  label: {
    measuringUnit: UI_LABEL_MEASURING_UNIT,
    description: UI_LABEL_DESCRIPTION,
    packageName: UI_LABEL_PACKAGE_NAME,
  },
  action: {
    edit: UI_ACTION_EDIT,
    delete: UI_ACTION_DELETE,
  },
};

// ============================================================================
// Consolidated Messages Object
// ============================================================================

export const Messages = {
  grn: GrnMessages,
  auth: AuthMessages,
  migration: MigrationMessages,
  item: ItemMessages,
  export: ExportMessages,
  brand: BrandMessages,
  mainCategory: MainCategoryMessages,
  category: CategoryMessages,
  genericName: GenericNameMessages,
  subCategory: SubCategoryMessages,
  documentType: DocumentTypeMessages,
  measuringUnit: MeasuringUnitMessages,
  package: PackageMessages,
  ui: UITextMessages,
} as const;

// Type for the consolidated messages object
export type MessagesType = typeof Messages;

